/**
 * Policies — the web's copy of the API contract (the 2026-10-14 rebuild).
 *
 * HumanFirewall is a human-risk-management and security-awareness-training
 * platform. The Policies module lets an organisation put its own policy PDFs in
 * front of its own staff: a maker uploads one, a different person approves it,
 * employees read the pages, tick the key points, pass the optional questions and
 * acknowledge it, and every acknowledgment is kept as audit evidence.
 *
 * Contract: spec/backend-design.md §9 (routes, shapes, codes), §3 (enums).
 *
 * Mirrors, field for field:
 *  - apps/api/src/modules/policies/dto/policy-vocabulary.ts       (the tuples)
 *  - apps/api/src/modules/policies/dto/policy-responses.types.ts  (the responses)
 *  - apps/api/src/modules/policies/dto/policy-error-codes.ts      (POLICY_ERROR_CODES)
 *  - apps/api/src/modules/policies/dto/policy-issue-codes.ts      (POLICY_ISSUE_CODES)
 *  - the request DTOs in apps/api/src/modules/policies/dto/*.dto.ts (the bodies)
 *
 * Guards (never loosen one to get past a red):
 *  - __tests__/responses-match-the-api.guard.spec.ts reads the API files from disk:
 *    every response declaration and every vocabulary tuple there must appear here,
 *    field for field, value for value.
 *  - components/policies/__tests__/labels-match-the-schema.guard.spec.ts holds each
 *    tuple that mirrors a pgEnum EQUAL to it and every label map complete (the
 *    2026-09-07 "sixth payload type" rule).
 *  - __tests__/errors-cover-every-code.spec.ts holds the error and issue codes equal
 *    to the API's and worded in en, ar, hi and fr.
 *
 * Conventions that hold on every response (backend §9.0):
 *  - instants are ISO `Z` strings; calendar days are WORKSPACE days `yyyy-MM-dd`; a
 *    response that carries days names the zone it counted them in (`timeZone`, and
 *    `today` where "today" matters);
 *  - a field is never optional: an absent value is `null`;
 *  - statuses travel in Title Case ('Not Started'); a screen words them through
 *    components/policies/labels.ts, never prints them;
 *  - percentages are whole numbers 0-100, or null when the denominator is 0. The
 *    browser never computes a KPI.
 *
 * FROZEN after the contract phase: changes go through the lead, who updates this
 * file and the API files in one commit.
 */

// ── Vocabulary (a verbatim copy of policy-vocabulary.ts) ────────────────────

// ── Languages ───────────────────────────────────────────────────────────────

/**
 * The interface languages. A policy is ONE PDF in the language its owner
 * wrote it in (DECISIONS L13: no multi-language content); these four are the
 * languages the acknowledgment statement, the mail, the CSV headings and the
 * receipt PDFs are written in.
 */
export const POLICY_LANGUAGES = ['en', 'ar', 'hi', 'fr'] as const
export type PolicyLanguage = (typeof POLICY_LANGUAGES)[number]

/** Languages written right to left (the receipt and the statement flip for these). */
export const POLICY_RTL_LANGUAGES: readonly PolicyLanguage[] = ['ar']

/** Whether a value is one of the four interface languages. */
export function isPolicyLanguage(value: unknown): value is PolicyLanguage {
  return typeof value === 'string' && (POLICY_LANGUAGES as readonly string[]).includes(value)
}

/**
 * v3 (DECISIONS D7): the LANGUAGE PACK languages offered first. A pack is the policy's text (title, summary,
 * change note, statement, key points, questions) translated into one language, approved like a version
 * (policy_language_packs), optionally with its own PDF of the same page count, and shown BESIDE the base PDF
 * ("side by side"). The data model takes any language tag (POLICY_LANGUAGE_TAG_PATTERN); these three are offered
 * because their acknowledgment statement templates, mail chrome and PDF fonts exist. Settings -> Languages turns
 * them on per workspace (policy_settings.enabled_languages; 400 policy_settings_invalid {field: enabledLanguages}
 * for any other value).
 */
export const POLICY_PACK_LANGUAGES = ['ar', 'hi', 'fr'] as const
export type PolicyPackLanguage = (typeof POLICY_PACK_LANGUAGES)[number]

/** Writing direction and script of each offered pack language (the receipt PDF picks its font by script). */
export const POLICY_PACK_LANGUAGE_FACTS: Readonly<Record<PolicyPackLanguage, { dir: 'rtl' | 'ltr'; script: 'Arabic' | 'Devanagari' | 'Latin' }>> = {
  ar: { dir: 'rtl', script: 'Arabic' },
  hi: { dir: 'ltr', script: 'Devanagari' },
  fr: { dir: 'ltr', script: 'Latin' },
}

/** A language tag as the database checks it (policies.base_language / display_language, packs, settings, test runs). */
export const POLICY_LANGUAGE_TAG_PATTERN = '^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$'

/**
 * v3 (A1): policies.document_id, the maker's own document reference (IV-ISMS-POL-012), CHECK policies_document_id.
 * Unique per COMPANY ignoring case among live policies (uq_policies_document_id; 409 policy_document_id_taken),
 * editable only while the policy is a Draft (409 policy_document_id_locked), required at submit (issue
 * document_id_missing). It is the receipt prefix: EV-{documentId}-{personSeq4}-v{major.minor}-C{cycle} (L15).
 */
export const POLICY_DOCUMENT_ID_PATTERN = '^[A-Za-z0-9][A-Za-z0-9._/-]{0,39}$'

// ── pgEnum mirrors (the schema owns the enums; values must match exactly) ──

/**
 * pgEnum `policy_status` — the policy identity's lifecycle.
 *  Draft     — no version was ever published (an untitled draft is not listed).
 *  Published — has a current version.
 *  Archived  — retired, with a reason (archive_reason, L4); read-only; its evidence stays. It is published
 *              again only by a NEW version through approval (L4: "Update" on an archived policy).
 */
export const POLICY_STATUSES = ['Draft', 'Published', 'Archived'] as const
export type PolicyStatus = (typeof POLICY_STATUSES)[number]

/**
 * pgEnum `policy_version_status`. Editable only in `Draft` and `Changes Requested`
 * (a database trigger holds that from submit onward: 409 policy_version_locked).
 */
export const POLICY_VERSION_STATUSES = [
  'Draft',
  'Awaiting Approval',
  'Changes Requested',
  'Published',
  'Superseded',
  'Archived',
] as const
export type PolicyVersionStatus = (typeof POLICY_VERSION_STATUSES)[number]

/** The version statuses a maker may still edit. */
export const POLICY_EDITABLE_VERSION_STATUSES: readonly PolicyVersionStatus[] = ['Draft', 'Changes Requested']

/** The version statuses of a version still being prepared or reviewed (at most one per policy). */
export const POLICY_OPEN_VERSION_STATUSES: readonly PolicyVersionStatus[] = ['Draft', 'Awaiting Approval', 'Changes Requested']

/** The Drafts tab's status facet. */
export const POLICY_DRAFT_STATUSES = ['Draft', 'Awaiting Approval', 'Changes Requested'] as const
export type PolicyDraftStatus = (typeof POLICY_DRAFT_STATUSES)[number]

/**
 * pgEnum `policy_change_type`. The maker CHOOSES Material or Minor (independent
 * of the number): Material opens the next round and everyone acknowledges
 * again; Minor keeps the round and existing acknowledgments still count.
 * v3 (D9, E1): the UI says "New version" (Material: a higher major.minor) and "Revision" (Minor: the SAME
 * major.minor, revision + 1, read "1.0 rev 2"); a Revision may not change any key point (issue
 * revision_promises_changed). CHECK policy_versions_revision: (change_type = 'Minor') = (revision > 1).
 */
export const POLICY_CHANGE_TYPES = ['First Issue', 'Material', 'Minor'] as const
export type PolicyChangeType = (typeof POLICY_CHANGE_TYPES)[number]

/** The change types a maker chooses between on a new version (a first issue is never chosen). */
export const POLICY_CHOSEN_CHANGE_TYPES = ['Material', 'Minor'] as const
export type PolicyChosenChangeType = (typeof POLICY_CHOSEN_CHANGE_TYPES)[number]

/** pgEnum `policy_assignment_origin` — what started an assignment row. */
export const POLICY_ASSIGNMENT_ORIGINS = ['Admin', 'Material Version', 'Ask Again', 'Workflow', 'Phishing'] as const
export type PolicyAssignmentOrigin = (typeof POLICY_ASSIGNMENT_ORIGINS)[number]

/** pgEnum `policy_due_mode`. */
export const POLICY_DUE_MODES = ['Days From Today', 'Fixed Date'] as const
export type PolicyDueMode = (typeof POLICY_DUE_MODES)[number]

/**
 * pgEnum `policy_attestation_status` — where one person stands on one policy in one round.
 * v3 'Exempted' (E3, L7): an approved exemption covers the row until its end day (exempted_at, exemption_id; CHECK
 * policy_attestations_exempted). An exempted row is neither live nor pending: it leaves every count (tracking and
 * compliance), gets no mail, is carried into new rounds as Exempted, and is never a late joiner. When the exemption
 * ends (end day reached, or ended by hand) the row returns to the status it would have had.
 */
export const POLICY_ATTESTATION_STATUSES = [
  'Not Started',
  'In Progress',
  'Needs Help',
  'Acknowledged',
  'Superseded',
  'Removed',
  'Exempted',
  'Archived Before Acknowledgment',
] as const
export type PolicyAttestationStatus = (typeof POLICY_ATTESTATION_STATUSES)[number]

/** Rows still waiting for the person (reminders skip Needs Help). */
export const POLICY_PENDING_ATTESTATION_STATUSES: readonly PolicyAttestationStatus[] = [
  'Not Started',
  'In Progress',
  'Needs Help',
]

/** "Live" rows: the ones that count as assigned (an eligible person on a live row). */
export const POLICY_LIVE_ATTESTATION_STATUSES: readonly PolicyAttestationStatus[] = [
  'Not Started',
  'In Progress',
  'Needs Help',
  'Acknowledged',
]

/** Rows a reminder or a bulk "Send reminder" may reach (Needs Help gets Reset Attempts instead). */
export const POLICY_REMINDABLE_ATTESTATION_STATUSES: readonly PolicyAttestationStatus[] = ['Not Started', 'In Progress']

/**
 * pgEnum `policy_removal_reason`. v3 (D10, L7): only these two; "on leave" and "exempted by the owner" are an
 * EXEMPTION now. Remove is allowed only within POLICY_LIMITS.removeWindowDays of the row's assignment and before the
 * person opens it (409 policy_remove_not_allowed {reason}); leavers drop out by eligibility on their own.
 */
export const POLICY_REMOVAL_REASONS = ['Left The Company', 'Assigned By Mistake'] as const
export type PolicyRemovalReason = (typeof POLICY_REMOVAL_REASONS)[number]

/** pgEnum `policy_question_type`. True/false answers are fixed (`fixed` 'True' | 'False'). */
export const POLICY_QUESTION_TYPES = ['Single Choice', 'Multiple Choice', 'True Or False'] as const
export type PolicyQuestionType = (typeof POLICY_QUESTION_TYPES)[number]

/** The fixed answers of a True Or False question (the portal translates them). */
export const POLICY_FIXED_ANSWERS = ['True', 'False'] as const
export type PolicyFixedAnswer = (typeof POLICY_FIXED_ANSWERS)[number]

/** pgEnum `policy_event_kind` — one per thing that happens on a person's row. APPEND-ONLY timeline. */
export const POLICY_EVENT_KINDS = [
  'Assigned',
  'Email Sent',
  'Email Not Sent',
  'Reminder Sent',
  'Opened',
  'Page Counted',
  'Read Every Page',
  'Key Point Ticked',
  'Key Point Unticked',
  'Key Points Confirmed',
  'Attempt Started',
  'Attempt Submitted',
  'Needs Help',
  'Owner Notified',
  'Attempts Reset',
  'Due Date Changed',
  'Content Updated',
  'Superseded',
  'Removed',
  'Restored',
  'Archived Before Acknowledgment',
  'Acknowledged',
  // v3 (E3): an exemption's life on the person's row (detail: { exemptionId, endsOn, scope }).
  'Exemption Requested',
  'Exemption Declined',
  'Exemption Withdrawn',
  'Exempted',
  'Exemption Ended',
  // v3 (E2): an "Acknowledged Only" re-acknowledgment kept this pending person's task, and their progress, on the
  // new round's row (detail: { fromRound, fromAttestationId }).
  'Progress Carried Forward',
] as const
export type PolicyEventKind = (typeof POLICY_EVENT_KINDS)[number]

/** pgEnum `policy_event_actor`. */
export const POLICY_EVENT_ACTORS = ['Recipient', 'User', 'System'] as const
export type PolicyEventActor = (typeof POLICY_EVENT_ACTORS)[number]

/**
 * pgEnum `policy_identification_method` — how the person was identified when
 * they acknowledged. Customer-facing on the receipt, hence Title Case.
 * `portal_sessions.sign_in_method` keeps its internal varchar tokens
 * (`portalIdentificationMethod()` converts them).
 */
export const POLICY_IDENTIFICATION_METHODS = [
  'Portal SSO',
  'Portal Magic Link',
  'Portal OTP',
  'Portal Password',
  'Signed Link',
] as const
export type PolicyIdentificationMethod = (typeof POLICY_IDENTIFICATION_METHODS)[number]

/** pgEnum `policy_ai_run_status`. */
export const POLICY_AI_RUN_STATUSES = ['Running', 'Succeeded', 'No Result', 'Failed'] as const
export type PolicyAiRunStatus = (typeof POLICY_AI_RUN_STATUSES)[number]

/** pgEnum `policy_ai_suggestion_status`. */
export const POLICY_AI_SUGGESTION_STATUSES = ['Proposed', 'Accepted', 'Dismissed', 'Superseded'] as const
export type PolicyAiSuggestionStatus = (typeof POLICY_AI_SUGGESTION_STATUSES)[number]

/**
 * pgEnum `policy_archive_reason` (v3 L4): why a policy was archived (policies.archive_reason; the Archived tab's
 * Reason column). 'Other' needs a note (policies.archive_note, CHECK policies_archive). 'Replaced By Another Policy'
 * and 'Merged Into Another Policy' may name the policy (policies.archive_replaced_by_id).
 */
export const POLICY_ARCHIVE_REASONS = ['Replaced By Another Policy', 'No Longer Applies', 'Merged Into Another Policy', 'Other'] as const
export type PolicyArchiveReason = (typeof POLICY_ARCHIVE_REASONS)[number]

/**
 * pgEnum `policy_reack_who` (v3 E2, D11): who an "Ask everyone to re-acknowledge" cycle asks.
 *  Everyone Assigned  — every live person of the round gets a fresh row (today's behaviour).
 *  Acknowledged Only  — only those who acknowledged are asked again; a pending person KEEPS their task and their
 *                       progress, carried onto the new round's row ('Progress Carried Forward').
 */
export const POLICY_REACK_WHO = ['Everyone Assigned', 'Acknowledged Only'] as const
export type PolicyReackWho = (typeof POLICY_REACK_WHO)[number]

/** pgEnum `policy_exemption_status` (v3 E3, D10, L7). Requested and Approved are LIVE (one live per person and scope). */
export const POLICY_EXEMPTION_STATUSES = ['Requested', 'Approved', 'Declined', 'Withdrawn', 'Ended'] as const
export type PolicyExemptionStatus = (typeof POLICY_EXEMPTION_STATUSES)[number]

/** pgEnum `policy_pack_item_kind` (v3 languages): what one pack item translates. */
export const POLICY_PACK_ITEM_KINDS = ['Title', 'Summary', 'Change Note', 'Statement', 'Key Point', 'Question'] as const
export type PolicyPackItemKind = (typeof POLICY_PACK_ITEM_KINDS)[number]

/** pgEnum `policy_pack_item_origin`: the AI translation, the maker, or carried unchanged from the pack of an earlier version (L13 chips). */
export const POLICY_PACK_ITEM_ORIGINS = ['AI', 'Maker', 'Carried'] as const
export type PolicyPackItemOrigin = (typeof POLICY_PACK_ITEM_ORIGINS)[number]

/** pgEnum `policy_pack_item_change`: how the item's BASE text differs from the pack this one was updated from. */
export const POLICY_PACK_ITEM_CHANGES = ['New', 'Changed', 'Unchanged'] as const
export type PolicyPackItemChange = (typeof POLICY_PACK_ITEM_CHANGES)[number]

/** pgEnum `policy_test_run_status` (v3 C2 "send a test to myself"; never an attestation). */
export const POLICY_TEST_RUN_STATUSES = ['Queued', 'Sent', 'Not Sent', 'Opened', 'Finished'] as const
export type PolicyTestRunStatus = (typeof POLICY_TEST_RUN_STATUSES)[number]

/** pgEnum `policy_audit_requester_kind` (v3 G5/G6): a workspace user (internal builder) or an external auditor. */
export const POLICY_AUDIT_REQUESTER_KINDS = ['User', 'Auditor'] as const
export type PolicyAuditRequesterKind = (typeof POLICY_AUDIT_REQUESTER_KINDS)[number]

/** pgEnum `policy_audit_report_status`. A Ready report's files expire POLICY_LIMITS.auditReportLinkDays later. */
export const POLICY_AUDIT_REPORT_STATUSES = ['Queued', 'Building', 'Ready', 'Failed', 'Expired'] as const
export type PolicyAuditReportStatus = (typeof POLICY_AUDIT_REPORT_STATUSES)[number]

// ── Text + CHECK vocabularies (owned here; the migration copies them) ──────

/**
 * policy_versions.category (text, CHECK `policy_versions_category`) — Kumar's
 * five (DECISIONS L6). Drives the cover art family.
 */
export const POLICY_CATEGORIES = ['Information Security', 'Acceptable Use', 'Privacy', 'HR', 'Other'] as const
export type PolicyCategory = (typeof POLICY_CATEGORIES)[number]

/**
 * Allow-listed cover illustration keys. The web maps each to an illustration
 * with a fallback tint (apps/web/lib/policies/covers.ts); the artwork guard
 * (tests/unit/tooling/surveys-policies-illustration-briefs.guard.spec.ts)
 * holds this list, so it keeps its 13 drawn keys.
 */
export const POLICY_COVER_KEYS = [
  'policy-information-security',
  'policy-acceptable-use',
  'policy-data-protection',
  'policy-access-control',
  'policy-ai-use',
  'policy-remote-working',
  'policy-clean-desk',
  'policy-email-internet',
  'policy-mobile-devices',
  'policy-passwords',
  'policy-incident-response',
  'policy-code-of-conduct',
  'policy-general',
] as const
export type PolicyCoverKey = (typeof POLICY_COVER_KEYS)[number]

/** The default cover per category (used when cover_key is null). Existing art reused (admin spec D6). */
export const POLICY_DEFAULT_COVER_BY_CATEGORY: Readonly<Record<PolicyCategory, PolicyCoverKey>> = {
  'Information Security': 'policy-information-security',
  'Acceptable Use': 'policy-acceptable-use',
  Privacy: 'policy-data-protection',
  HR: 'policy-code-of-conduct',
  Other: 'policy-general',
}

/** The pass marks a maker may choose (percent). */
export const POLICY_PASS_MARKS = [60, 70, 75, 80, 90, 100] as const
/**
 * The re-acknowledgment windows (days, D11): a Material version's reack_days (CHECK policy_versions_reack) and an
 * "Ask everyone to re-acknowledge" dueDays. Default POLICY_LIMITS.reackDaysDefault.
 */
export const POLICY_REACK_DAYS = [7, 14, 30, 60, 90] as const
/** The days a bulk "Extend due date" may add. */
export const POLICY_EXTEND_DAYS = [7, 14, 30] as const

/** v3 (D12): the reading minimum per page a workspace chooses (policy_settings.page_min_seconds, CHECK policy_settings_sending). */
export const POLICY_PAGE_MIN_SECONDS = [5, 10, 15, 30] as const

/**
 * v3 (L8): how many years the evidence is kept (policy_settings.evidence_retention_years, CHECK
 * policy_settings_retention). Shortening it takes effect POLICY_LIMITS.retentionShortenNoticeDays later
 * (retention_pending_years + retention_change_effective_at); lengthening applies at once. Wording says "kept for N
 * years", never that anything is deleted automatically (no deletion job exists).
 */
export const POLICY_RETENTION_YEARS = [3, 5, 7, 10] as const

/**
 * v3 (D8, L1, L2): the compliance mode of a ROUND, chosen by whatever opens it (the first Admin assignment of a first
 * issue, a Material publish, an Ask Again) and inherited by every later assignment of that round. Stored as
 * policy_assignments.counts_toward_compliance on the round opener (opens_round = true), and the workspace default as
 * text in policy_settings.default_compliance_mode (CHECK policy_settings_sending). 'Acknowledgment Only' rounds are in
 * no compliance figure until an approver makes them count (one-way; 'Compliance Mode Changed').
 */
export const POLICY_COMPLIANCE_MODES = ['Counts Toward Compliance', 'Acknowledgment Only'] as const
export type PolicyComplianceMode = (typeof POLICY_COMPLIANCE_MODES)[number]

// ── Detail vocabularies (inside policy_attestation_events.detail; not enums) ─

/** `Reminder Sent` detail.trigger. */
export const POLICY_REMINDER_TRIGGERS = [
  'Three Days After Assigning',
  'Seven Days After Assigning',
  'On The Due Date',
  'Three Days Overdue',
  'Manual',
] as const
export type PolicyReminderTrigger = (typeof POLICY_REMINDER_TRIGGERS)[number]

/**
 * `Email Sent` / `Email Not Sent` detail.mail. v3: 'Re-acknowledgment Request' is the Ask Again notice (catalogue key
 * policy_new_version, worded "Re-acknowledgment request", with sender_name and request_note, never a change summary).
 */
export const POLICY_MAIL_KINDS = ['Assignment', 'New Version', 'Due Date Changed', 'Attempts Reset', 'Receipt', 'Needs Help', 'Re-acknowledgment Request'] as const
export type PolicyMailKind = (typeof POLICY_MAIL_KINDS)[number]

/**
 * v3: every central catalogue key a Policies email is sent under (notification-catalog.ts; DECISIONS D3: the words
 * live only in the Email Centre). The first twelve exist; the last eight are reserved for P06 (L18 and the reminder
 * split): policy_reminder stays the 3- and 7-day reminder, policy_due_today and policy_overdue are the due-day and
 * overdue reminders. The Templates page (W2) lists these, in this order.
 */
export const POLICY_MAIL_KEYS = [
  'policy_assigned',
  'policy_new_version',
  'policy_reminder',
  'policy_remedial',
  'policy_due_date_changed',
  'policy_attempts_reset',
  'policy_receipt',
  'policy_needs_help',
  'policy_approval_requested',
  'policy_changes_requested',
  'policy_published',
  'policy_review_due',
  'policy_due_today',
  'policy_overdue',
  'policy_exemption_requested',
  'policy_exemption_decided',
  'policy_auditor_invited',
  'policy_auditor_invited_notice',
  'policy_audit_pack_ready',
  'policy_manager_digest',
] as const
export type PolicyMailKey = (typeof POLICY_MAIL_KEYS)[number]

/** `Opened` detail.source. */
export const POLICY_OPEN_SOURCES = ['Portal', 'Email Link', 'Signed Link'] as const
export type PolicyOpenSource = (typeof POLICY_OPEN_SOURCES)[number]

// ── Request vocabularies ────────────────────────────────────────────────────

/** GET /library?section= */
export const POLICY_LIBRARY_SECTIONS = ['published', 'drafts', 'archived'] as const
export type PolicyLibrarySection = (typeof POLICY_LIBRARY_SECTIONS)[number]

/** The published tab's sortable columns (v3 adds 'acknowledged': the Acknowledged column, by figures.acknowledgedRate). */
export const POLICY_LIBRARY_SORT_FIELDS = ['title', 'recipients', 'compliance', 'acknowledged', 'due'] as const
export type PolicyLibrarySortField = (typeof POLICY_LIBRARY_SORT_FIELDS)[number]

/** The Recipients tab's status facet. 'All' = no filter. v3 adds 'Exempted'. */
export const POLICY_PEOPLE_STATUS_FILTERS = [
  'All',
  'Acknowledged',
  'In Progress',
  'Not Started',
  'Needs Help',
  'Overdue',
  'Superseded',
  'Removed',
  'Exempted',
  'Left The Company',
  'Archived Before Acknowledgment',
] as const
export type PolicyPeopleStatusFilter = (typeof POLICY_PEOPLE_STATUS_FILTERS)[number]

/** The Recipients tab's sortable columns. */
export const POLICY_PEOPLE_SORT_FIELDS = ['name', 'status', 'due', 'opened', 'acknowledged', 'score'] as const
export type PolicyPeopleSortField = (typeof POLICY_PEOPLE_SORT_FIELDS)[number]

/** The activity chart's buckets. */
export const POLICY_ACTIVITY_BUCKETS = ['Days', 'Weeks'] as const
export type PolicyActivityBucket = (typeof POLICY_ACTIVITY_BUCKETS)[number]

/** Why a round opened (PolicyDetail.rounds[].reason). */
export const POLICY_ROUND_REASONS = ['First Publish', 'Material Version', 'Ask Again'] as const
export type PolicyRoundReason = (typeof POLICY_ROUND_REASONS)[number]

/** What a Drafts-tab row offers the viewer. */
export const POLICY_DRAFT_VIEWER_ACTIONS = ['Review', 'View', 'Fix And Resubmit', 'Continue Editing'] as const
export type PolicyDraftViewerAction = (typeof POLICY_DRAFT_VIEWER_ACTIONS)[number]

/** v3 (F2): what a "Drafts and approvals" row is: a version of a policy, or a language pack of one. */
export const POLICY_DRAFT_KINDS = ['Version', 'Language Pack'] as const
export type PolicyDraftKind = (typeof POLICY_DRAFT_KINDS)[number]

/**
 * v3 (F2, A8): one language of a policy as the library chips and the Languages card show it.
 *  Base            the policy's own language (policies.base_language): always first, always present.
 *  Published       a Published pack exists for the CURRENT version.
 *  Needs Updating  the newest Published pack is on an older version and none is Published on the current one (L-5:
 *                  employees see the base language only until it is updated).
 *  In Progress     a Draft, Awaiting Approval or Changes Requested pack exists (and nothing Published is current).
 */
export const POLICY_LANGUAGE_STATES = ['Base', 'Published', 'Needs Updating', 'In Progress'] as const
export type PolicyLanguageState = (typeof POLICY_LANGUAGE_STATES)[number]

/**
 * v3 (H4, D6): how the employee flow shows a policy. 'Side By Side' only when policies.display_language names a
 * language with a Published pack on the row's version; a stale pack falls back to 'Base Only' on its own.
 */
export const POLICY_DISPLAY_MODES = ['Base Only', 'Side By Side'] as const
export type PolicyDisplayMode = (typeof POLICY_DISPLAY_MODES)[number]

/**
 * v3 (C1, C2): what the employee flow records (PortalPolicyTask.recording).
 *  Live     the real task: every step is a write.
 *  View As  an admin's read-only "View as" session (PortalPolicyTask.preview = true; 403 policy_preview_read_only).
 *  Preview  the admin's full-screen "Preview as employee" (GET …/versions/:versionId/preview): nothing is recorded,
 *           progress lives in the browser, attempts and grading are stateless server calls.
 *  Test     a "send a test to myself" link (/v1/public/policy-tests/:testId/:sig): only the test run row is stamped.
 */
export const POLICY_TASK_RECORDINGS = ['Live', 'View As', 'Preview', 'Test'] as const
export type PolicyTaskRecording = (typeof POLICY_TASK_RECORDINGS)[number]

/** v3 (D1): where an Admin assignment's sending stands (PolicySendingAssignment / PolicyAssignmentRow.sendState). */
export const POLICY_SEND_STATES = ['Waiting', 'Sent', 'Cancelled'] as const
export type PolicySendState = (typeof POLICY_SEND_STATES)[number]

/** v3 (E3, L7): an exemption covers one policy, or every published policy of the workspace (policy_exemptions.policy_id NULL). */
export const POLICY_EXEMPTION_SCOPES = ['This Policy', 'All Policies'] as const
export type PolicyExemptionScope = (typeof POLICY_EXEMPTION_SCOPES)[number]

/** v3 (L9): the evidence integrity line ("Integrity: Verified - last checked …"). 'Not Checked' before the first nightly check. */
export const POLICY_INTEGRITY_STATES = ['Verified', 'Broken', 'Not Checked'] as const
export type PolicyIntegrityState = (typeof POLICY_INTEGRITY_STATES)[number]

/**
 * v3 (L3): the dashboard's "Needs your attention" items, in the order they are listed. Each carries the ids its inline
 * action needs (PolicyAttentionItem).
 */
export const POLICY_ATTENTION_KINDS = [
  'Approval Waiting',
  'Language Pack Waiting',
  'Exemption To Decide',
  'Emails Waiting',
  'Overdue',
  'Needs Help',
  'Review Due',
  'Language Needs Updating',
  'Owner Needed',
  'Late Joiners',
] as const
export type PolicyAttentionKind = (typeof POLICY_ATTENTION_KINDS)[number]

/**
 * v3 (G5, L10): the files of an audit report, in the order "Files in this report" lists them. 'Receipts' (a folder of
 * receipt PDFs, capped at POLICY_LIMITS.auditReportReceiptsMax) only when the builder asked for them; 'Policy
 * Documents' (the version PDFs) always; 'Zip' is everything in one archive.
 */
export const POLICY_AUDIT_REPORT_FILE_KINDS = [
  'Audit Report',
  'Policy Register',
  'Acknowledgment Records',
  'Evidence Events',
  'Emails Sent',
  'Exemptions',
  'Admin Audit Log',
  'Policy Documents',
  'Receipts',
  'Zip',
] as const
export type PolicyAuditReportFileKind = (typeof POLICY_AUDIT_REPORT_FILE_KINDS)[number]

/**
 * v3 (L10): how the internal audit-report builder picks its POLICIES (policy_audit_reports.options.scope): the ones
 * listed, every published one of a category, or every published one carrying a control tag. An auditor's report is
 * always 'Policies' (the granted ones, or a subset of them).
 */
export const POLICY_AUDIT_REPORT_SCOPES = ['Policies', 'Category', 'Control Tag'] as const
export type PolicyAuditReportScope = (typeof POLICY_AUDIT_REPORT_SCOPES)[number]

/**
 * v3 (L10): which PEOPLE an audit report covers (policy_audit_reports.options.people): everyone in the period, or only
 * a pasted list of e-mail addresses (options.emails, at most POLICY_LIMITS.auditReportEmailsMax; internal builder only).
 */
export const POLICY_AUDIT_REPORT_PEOPLE = ['Everyone', 'Listed People'] as const
export type PolicyAuditReportPeople = (typeof POLICY_AUDIT_REPORT_PEOPLE)[number]

/**
 * v3 (G2): an external auditor's access, derived (never stored): Revoked when revoked_at is set, else Expired once
 * access_ends_at has passed, else Active.
 */
export const POLICY_AUDITOR_STATUSES = ['Active', 'Expired', 'Revoked'] as const
export type PolicyAuditorStatus = (typeof POLICY_AUDITOR_STATUSES)[number]

/** v3 (L9): where the nightly integrity check found its first break (policy_evidence_checks.first_break.kind). */
export const POLICY_EVIDENCE_BREAK_KINDS = ['Proof Chain', 'Timeline', 'Timeline Head'] as const
export type PolicyEvidenceBreakKind = (typeof POLICY_EVIDENCE_BREAK_KINDS)[number]

/** The funnel stages the Reports tab draws (every stage counts the acknowledged as having reached it). */
export const POLICY_FUNNEL_STAGES = ['Assigned', 'Opened', 'Read Every Page', 'Key Points Confirmed', 'Passed Questions', 'Acknowledged'] as const
export type PolicyFunnelStage = (typeof POLICY_FUNNEL_STAGES)[number]

/** The version-editor steps an issue belongs to (H's order). */
export const POLICY_ISSUE_STEPS = ['What', 'Confirm', 'Ask'] as const
export type PolicyIssueStep = (typeof POLICY_ISSUE_STEPS)[number]

/** The employee flow's steps, in order (questions are skipped when the version has none). */
export const POLICY_FLOW_STEPS = ['Read', 'Key Points', 'Questions', 'Acknowledge'] as const
export type PolicyFlowStep = (typeof POLICY_FLOW_STEPS)[number]

/** How an item differs from the version it was drafted from. */
export const POLICY_DIFF_CHANGES = ['New', 'Changed', 'Unchanged', 'Removed'] as const
export type PolicyDiffChange = (typeof POLICY_DIFF_CHANGES)[number]

/**
 * The automatic checks the review page lists (PolicyReviewCheck.key; admin
 * spec §9). 'File Differs' and 'Version Higher' appear on a new version only.
 */
export const POLICY_REVIEW_CHECK_KEYS = [
  'File Differs',
  'Version Higher',
  'Pdf Readable',
  'Key Points Cite Pages',
  'Questionnaire',
  // v3 (L12): a Revision's changed pages carry no changed number and no changed must/shall/required (the free,
  // deterministic check over the page text). params: { pages: number[], overridden: boolean, reason: string | null }.
  // ok = false with overridden = true when the maker gave revision_override_reason (the approver reads it).
  'Revision Rules Unchanged',
] as const
export type PolicyReviewCheckKey = (typeof POLICY_REVIEW_CHECK_KEYS)[number]

/**
 * Fields the prefill from a document-control table can fill (and v3's "Fill with AI" Details run, which fills only
 * EMPTY fields: PolicyVersionDetail.prefilledByAi names the ones it filled).
 */
export const POLICY_PREFILL_FIELDS = ['title', 'effectiveOn', 'reviewBy', 'summary', 'category', 'documentId', 'managementApproval'] as const
export type PolicyPrefillField = (typeof POLICY_PREFILL_FIELDS)[number]

/**
 * The AI run kinds a policy_ai_runs row can hold (varchar, DTO-validated, deliberately no CHECK). v3 adds two that
 * have their OWN routes and write no suggestions:
 *   Details      "Fill with AI" on the details step: fills only the empty details fields from the PDF (P02).
 *   Translation  a language pack's AI translation into the Draft pack's items (P07; policy_ai_runs.pack_id, language).
 */
export const POLICY_AI_RUN_KINDS = [
  'Key Points',
  'Questions',
  'Version Impact',
  'Change Note',
  'Miss Rate Insight',
  'Review Consistency',
  'Details',
  'Translation',
] as const
export type PolicyAiRunKind = (typeof POLICY_AI_RUN_KINDS)[number]

/** The kinds POST …/versions/:versionId/ai/runs accepts (RunPolicyAiDto): the ones that write suggestions. */
export const POLICY_AI_SUGGESTION_RUN_KINDS = [
  'Key Points',
  'Questions',
  'Version Impact',
  'Change Note',
  'Miss Rate Insight',
  'Review Consistency',
] as const
export type PolicyAiSuggestionRunKind = (typeof POLICY_AI_SUGGESTION_RUN_KINDS)[number]

/** policy_ai_suggestions.kind (varchar, DTO-validated, no CHECK). */
export const POLICY_AI_SUGGESTION_KINDS = [
  'Key Point',
  'Question',
  'Key Point Update',
  'Question Update',
  'Change Note',
  'Insight',
] as const
export type PolicyAiSuggestionKind = (typeof POLICY_AI_SUGGESTION_KINDS)[number]

// ── Refusal parameters (what a refusal's `field` / `reason` can be) ─────────

/**
 * `policy_field_invalid {field}` (400): the field of the version PATCH that holds a value the DTO shape allows but
 * the rule does not. Each has its own sentence in the web (policies.errors.fieldInvalid.<field>), so the screen says
 * which field instead of "one of the values is not valid". Contract review finding 14.
 *   category                not one of POLICY_CATEGORIES
 *   changeType              sent on a first issue (a first issue is always First Issue); v3: 'Minor' (a Revision)
 *                           of an ARCHIVED policy (publishing it again is always a new version, L4)
 *   reackDays               not one of POLICY_REACK_DAYS, or sent on a Minor version (Material only)
 *   passMark                not one of POLICY_PASS_MARKS
 *   attemptsAllowed         outside POLICY_LIMITS.attemptsMin to attemptsMax
 * v3:
 *   documentId              not POLICY_DOCUMENT_ID_PATTERN (taken / locked have their own codes)
 *   controlTags             more than POLICY_LIMITS.controlTagsMax, or a tag empty or over controlTagMax characters
 *   reviewBy                not after today (metadata PATCH, L11)
 *   nextReviewOn            "Mark as reviewed": not after today, or more than POLICY_LIMITS.reviewNextMaxDays ahead (L5)
 *   countsTowardCompliance  false on a version that is not Material (only a new version can be acknowledgment-only)
 *   revisionOverrideReason  sent on a version that is not a Revision (L12)
 *   managementApprovedOn    after today
 * (A malformed version number is not here: it has its own codes, policy_version_number_invalid / _not_higher.)
 */
export const POLICY_FIELD_INVALID_FIELDS = [
  'category',
  'changeType',
  'reackDays',
  'passMark',
  'attemptsAllowed',
  'documentId',
  'controlTags',
  'reviewBy',
  'nextReviewOn',
  'countsTowardCompliance',
  'revisionOverrideReason',
  'managementApprovedOn',
] as const
export type PolicyFieldInvalidField = (typeof POLICY_FIELD_INVALID_FIELDS)[number]

/**
 * `policy_question_invalid {reason}` (400): a question the PATCH refuses to store. Everything else about a question
 * that is wrong while the maker is still typing (an empty text, no correct answer, two equal answers) is an issue, not a
 * refusal: autosave must keep what was typed.
 *   answers_count  fewer than POLICY_LIMITS.answersMin or more than answersMax answers (True Or False is fixed by the server)
 *   one_correct    a Single Choice or True Or False question with MORE than one correct answer (none correct is the
 *                  issue question_correct_missing; grading wants every correct answer and no wrong one, so two correct
 *                  flags could never be passed). AI suggestions and the assistant write the same PATCH.
 */
export const POLICY_QUESTION_INVALID_REASONS = ['answers_count', 'one_correct'] as const
export type PolicyQuestionInvalidReason = (typeof POLICY_QUESTION_INVALID_REASONS)[number]

/**
 * `policy_owner_invalid {reason}` (400): the owner (who gets Needs-help alerts) the PATCH refuses.
 *   not_a_member  not a user of this workspace (unknown, deleted, or another workspace's)
 *   inactive      a user who cannot sign in (deactivated or suspended)
 */
export const POLICY_OWNER_INVALID_REASONS = ['not_a_member', 'inactive'] as const
export type PolicyOwnerInvalidReason = (typeof POLICY_OWNER_INVALID_REASONS)[number]

/**
 * v3 `policy_self_approval {reason}` (403): why the caller may not decide their own item (DECISIONS D5, D10).
 *   setting_off    "Approvers can approve their own work" is off for the workspace (policy_settings.self_approval_allowed)
 *   not_named      the setting is on, but the submitter did not name themselves as the approver at submit
 *   own_exemption  the exemption is for the deciding user themselves (nobody exempts themselves, whatever the setting)
 */
export const POLICY_SELF_APPROVAL_REASONS = ['setting_off', 'not_named', 'own_exemption'] as const
export type PolicySelfApprovalReason = (typeof POLICY_SELF_APPROVAL_REASONS)[number]

/**
 * v3 settings fields (PATCH /settings; `policy_settings_invalid {field}` 400; the 'Settings Changed' audit row's
 * oldValues/newValues keys, which the web words through policies.settings.fields.<field>).
 */
export const POLICY_SETTINGS_FIELDS = [
  'selfApprovalAllowed',
  'enabledLanguages',
  'coolOffDefault',
  'coolOffMinutes',
  'signedLinksDefault',
  'pageMinSeconds',
  'defaultComplianceMode',
  'auditorDefaultDays',
  'auditorMaxDays',
  'evidenceRetentionYears',
  'managerDigestEnabled',
] as const
export type PolicySettingsField = (typeof POLICY_SETTINGS_FIELDS)[number]

/** v3 (L11): the policy METADATA, edited after publishing without an update ('Details Changed', old/new per field). */
export const POLICY_METADATA_FIELDS = ['owner', 'category', 'reviewBy', 'controlTags'] as const
export type PolicyMetadataField = (typeof POLICY_METADATA_FIELDS)[number]

/**
 * v3 `policy_remove_not_allowed {reason}` (409, L7): Remove is for a mistake caught early.
 *   opened         the person has opened the policy
 *   window_passed  more than POLICY_LIMITS.removeWindowDays since the row was assigned
 */
export const POLICY_REMOVE_REFUSAL_REASONS = ['opened', 'window_passed'] as const
export type PolicyRemoveRefusalReason = (typeof POLICY_REMOVE_REFUSAL_REASONS)[number]

/**
 * v3 `policy_archive_invalid {reason}` (400, L4).
 *   note_required         reason 'Other' without a note
 *   replacement_invalid   the replacing policy is not a live Published policy of this workspace, or is this one
 */
export const POLICY_ARCHIVE_INVALID_REASONS = ['note_required', 'replacement_invalid'] as const
export type PolicyArchiveInvalidReason = (typeof POLICY_ARCHIVE_INVALID_REASONS)[number]

/**
 * v3 `policy_exemption_invalid {reason}` (400, E3).
 *   ends_on        not after today, or more than POLICY_LIMITS.exemptionMaxDays ahead
 *   not_assigned   "This Policy" for a person with no live or exempted row on it
 *   note_required  the note is empty once trimmed
 */
export const POLICY_EXEMPTION_INVALID_REASONS = ['ends_on', 'not_assigned', 'note_required'] as const
export type PolicyExemptionInvalidReason = (typeof POLICY_EXEMPTION_INVALID_REASONS)[number]

/**
 * v3 `policy_auditor_invalid {field}` (400, G2).
 *   email     not an address
 *   days      access length outside 1 to the workspace's auditor_max_days
 *   policies  empty, more than POLICY_LIMITS.auditorPoliciesMax, or a policy that is not Published/Archived in this workspace
 */
export const POLICY_AUDITOR_INVALID_FIELDS = ['email', 'days', 'policies'] as const
export type PolicyAuditorInvalidField = (typeof POLICY_AUDITOR_INVALID_FIELDS)[number]

/**
 * v3 `policy_audit_report_invalid {reason}` (400, G5/G6).
 *   period          from after to, to after today, or longer than POLICY_LIMITS.auditReportPeriodMaxDays
 *   policies        none selected, a policy outside the grant (auditor) or not Published/Archived here
 *   scope_empty     the category / control tag / e-mail list picks no policy or nobody
 *   emails          more than POLICY_LIMITS.auditReportEmailsMax addresses
 */
export const POLICY_AUDIT_REPORT_INVALID_REASONS = ['period', 'policies', 'scope_empty', 'emails'] as const
export type PolicyAuditReportInvalidReason = (typeof POLICY_AUDIT_REPORT_INVALID_REASONS)[number]

/**
 * v3 `policy_audit_report_delete_not_allowed {reason}` (403; review R1 #3): an internal report is deleted by the person
 * who asked for it or by an approver; an external auditor's report is never deleted on the internal route.
 */
export const POLICY_AUDIT_REPORT_DELETE_REASONS = ['not_requester', 'auditor_report'] as const
export type PolicyAuditReportDeleteReason = (typeof POLICY_AUDIT_REPORT_DELETE_REASONS)[number]

// ── The audit log ───────────────────────────────────────────────────────────

/**
 * Every action the Policies module writes to the workspace audit log (backend-design 9.9; `AuditService.log`, module
 * `Policies`), as the API names them. The words are `workspaceAudit.actions.<action>` in the web's main catalogue (the
 * shared Audit Logs view reads them from there), so a row reads the same on the Audit Logs page, in the Dashboard's
 * Recent Activity and on a policy's Activity tab. Services import this tuple instead of typing the name by hand: a typo
 * would show a fallback label and break the action filter (contract review finding 9).
 */
export const POLICY_AUDIT_ACTIONS = [
  'Draft Created',
  'Draft Cloned',
  'PDF Uploaded',
  'PDF Replaced',
  'PDF Restored',
  'Draft Deleted',
  'Submitted For Approval',
  'Version Submitted',
  'Submission Withdrawn',
  'Changes Requested',
  'Approved And Published',
  'Version Published',
  'New Version Started',
  'Version Draft Discarded',
  'Assigned',
  'Asked Again',
  'Reminders Changed',
  'Due Date Changed',
  'Due Date Extended',
  'Reminder Sent',
  'Automatic Reminders',
  'Recipient Removed',
  'Recipient Restored',
  'Attempts Reset',
  'Owner Notified',
  'Policy Archived',
  'CSV Exported',
  'Evidence PDF Exported',
  'Receipt PDF Exported',
  'Chain Verified',
  'AI Suggestions Generated',
  'AI Suggestion Accepted',
  // ── v3 (2026-10-16). Owner package in brackets; detailParams named in CONTRACT.md. ──
  'Self-Approved And Published', // P02 (D5): the approver approved their own submission (entity PolicyVersion)
  'Details Changed', // P02 (L11): owner / category / review-by / control tags after publishing; oldValues/newValues
  'Marked As Reviewed', // P02 (L5): entity PolicyReview
  'AI Details Filled', // P02: the "Fill with AI" Details run filled empty fields
  'Settings Changed', // P01: entity PolicySettings; oldValues/newValues of the changed fields only
  'Language Pack Created', // P07: entity PolicyLanguagePack (also "Update for v1.1", detail updatedFromPackId)
  'Language Pack Submitted', // P07
  'Language Pack Withdrawn', // P07
  'Language Pack Changes Requested', // P07
  'Language Pack Published', // P07: approved by someone other than the submitter
  'Language Pack Self-Approved And Published', // P07 (D5)
  'Language Pack Deleted', // P07: a Draft pack deleted
  'AI Translation Generated', // P07: entity PolicyLanguagePack
  'Employees See Changed', // P07 (L17): policies.display_language old/new
  'Assignment Sent Now', // P04 (D1): a waiting assignment released before its time
  'Assignment Cancelled', // P04 (D1): a waiting assignment cancelled; nobody was assigned
  'Compliance Mode Changed', // P04 (D8): a round made to count (one-way)
  'Exemption Requested', // P04 (E3): entity PolicyExemption
  'Exemption Approved', // P04
  'Exemption Self-Approved', // P04 (D5, D10): approved by its own requester under the switch
  'Exemption Declined', // P04
  'Exemption Withdrawn', // P04
  'Exemption Ended', // P04: ended by hand (the end day passing is not audited)
  'Test Sent', // P08 (C2): entity PolicyTestRun
  'Auditor Invited', // P09 (G2): entity PolicyAuditor
  'Auditor Access Changed', // P09: end date or policies changed
  'Auditor Access Revoked', // P09
  'Auditor Invitation Resent', // P09
  'Auditor Signed In', // P09 (actor: the auditor)
  'Auditor Opened Policy', // P09 (actor: the auditor; written synchronously BEFORE content is served)
  'Auditor Opened Evidence', // P09
  'Auditor Downloaded File', // P09
  'Auditor Sign-In Locked', // R1 #2: the grant's wrong code checks are used up (actor: the grant, with the address)
  'Audit Report Requested', // P09 (G5/G6): entity PolicyAuditReport
  'Audit Report Built', // P09
  'Audit Report Downloaded', // P09
  'Audit Report Deleted', // R1 #3: written synchronously before the files are removed; metadata.files = kind + sha256
] as const
export type PolicyAuditAction = (typeof POLICY_AUDIT_ACTIONS)[number]

/** The entity types a Policies audit row carries (the Audit Logs view's "Entity" facet reads these). v3 adds seven. */
export const POLICY_AUDIT_ENTITY_TYPES = [
  'Policy',
  'PolicyVersion',
  'PolicyAssignment',
  'PolicyReminder',
  'PolicySettings',
  'PolicyLanguagePack',
  'PolicyExemption',
  'PolicyTestRun',
  'PolicyAuditor',
  'PolicyAuditReport',
  'PolicyReview',
] as const
export type PolicyAuditEntityType = (typeof POLICY_AUDIT_ENTITY_TYPES)[number]

// ── Limits ──────────────────────────────────────────────────────────────────

export const POLICY_LIMITS = {
  // File (backend-design §7)
  /** The product limit: a PDF over this is 413 policy_file_too_large. */
  maxFileBytes: 25 * 1024 * 1024,
  /** Multer's memory limit, so a 38 MB file can still be NAMED in the refusal. */
  uploadBufferBytes: 100 * 1024 * 1024,
  maxPages: 60,
  /** One more than maxPages, so a 61-page file is detected rather than truncated. */
  extractMaxPages: 61,
  extractMaxChars: 1_200_000,
  extractTimeoutMs: 30_000,
  extractMaxHeapMb: 512,
  /** A page "has text" with at least this many non-whitespace characters. */
  pageTextMinChars: 20,
  /** policy_file_no_text: fewer than this share of pages have text … */
  minTextPageShare: 0.5,
  /** … or the average is under this many characters per page. */
  minAverageCharsPerPage: 40,
  pageTitleMax: 120,
  // Details
  titleMax: 120,
  summaryMax: 200,
  changeNoteMax: 300,
  submitNoteMax: 300,
  reviewCommentMin: 5,
  reviewCommentMax: 500,
  removeNoteMax: 200,
  // v3 details (A1, L4, L5, L11, L12)
  documentIdMax: 40,
  managementApprovalMax: 200,
  controlTagsMax: 10,
  controlTagMax: 40,
  revisionMax: 99,
  revisionOverrideReasonMax: 300,
  archiveNoteMax: 200,
  reviewNoteMax: 300,
  /** "Mark as reviewed": the next review day is at most this many days ahead. */
  reviewNextMaxDays: 1096,
  // Key points and questions
  keyPointsMin: 3,
  keyPointsMax: 7,
  keyPointTextMax: 200,
  questionsMax: 10,
  questionTextMax: 300,
  explanationMax: 300,
  answersMin: 2,
  answersMax: 6,
  answerTextMax: 160,
  passMarkDefault: 80,
  attemptsMin: 1,
  attemptsMax: 5,
  attemptsDefault: 3,
  // Dates
  effectiveLeadDays: 7,
  reviewByLeadDays: 365,
  reackDaysDefault: 14,
  dueDaysMin: 1,
  dueDaysMax: 365,
  /** Remedial due override (Workflows action / campaign) — read by the workflow adapter. */
  remedialDueDaysMin: 1,
  remedialDueDaysMax: 90,
  // Reading
  /**
   * v1's page minimum (3 s). RETIRED by v3 (D12, H3): a page counts after the ROW's page_min_ms (snapshotted from
   * policy_settings.page_min_seconds when the person opens the task), else pageMinSecondsDefault * 1000. P05 moves
   * its readers to the row; the web reads the server's value (X1). Delete once nothing reads it (lead, integration).
   */
  pageViewMinMs: 3000,
  /** v3 (D12): the reading minimum when no settings row exists (policy_settings.page_min_seconds default). */
  pageMinSecondsDefault: 5,
  pageViewTokenMaxAgeMs: 30 * 60 * 1000,
  // Lists
  pageSizeOptions: [10, 25, 50] as readonly number[],
  pageSizeMax: 200,
  activityDaysMax: 45,
  sampleNamesMax: 3,
  lateJoinerIdsMax: 500,
  bulkPasteUnknownMax: 20,
  mostMissedMax: 3,
  mostMissedHighlightRate: 25,
  whereToActFirstMax: 4,
  recentActivityMax: 5,
  /** v3 (L3): "Needs your attention" items at most. */
  attentionMax: 12,
  /** Cells under this share of the department compliance heatmap read "act first". */
  actFirstBelowRate: 80,
  dueSoonDays: 14,
  // Evidence
  /** The default of policy_settings.evidence_retention_years (POLICY_RETENTION_YEARS); the workspace value wins. */
  evidenceRetentionYears: 7,
  /** L8: a shorter retention takes effect this many days after it is saved. */
  retentionShortenNoticeDays: 30,
  /** Receipt IDs (L15): EV-{documentId}-{recipient_seq padded}-v{major.minor}-C{cycle}; at most 80 characters. */
  receiptSeqPad: 4,
  receiptRefMax: 80,
  // Portal
  /** estimatedMinutes = max(5, round(pages * 1.5 + questions)). */
  estimatedMinutesMin: 5,
  estimatedMinutesPerPage: 1.5,
  // Batches
  mailBatch: 200,
  extendMax: 5000,
  remindMax: 5000,
  // v3 settings (F5; CHECKs policy_settings_*)
  coolOffMinutesMin: 1,
  coolOffMinutesMax: 120,
  coolOffMinutesDefault: 15,
  enabledLanguagesMax: 10,
  auditorDefaultDays: 30,
  auditorMaxDays: 180,
  /** The highest auditor_max_days a workspace may set (CHECK policy_settings_auditor; D13: 180 days at most). */
  auditorDaysCap: 180,
  // v3 sending, rounds and exemptions (D1, E2, E3, L7)
  /** Remove is allowed only this many days after the row was assigned, and only before the person opens it. */
  removeWindowDays: 7,
  requestNoteMax: 300,
  exemptionNoteMax: 300,
  exemptionDecisionNoteMax: 300,
  /** An exemption ends at most this many days after it is requested (D10: at most one year). */
  exemptionMaxDays: 365,
  exemptionBulkMax: 500,
  // v3 language packs (A2-A7): item lengths are 1.5 x the base item's (CHECK policy_language_pack_items_text)
  packTitleMax: 180,
  packSummaryMax: 300,
  packChangeNoteMax: 450,
  packStatementMax: 600,
  packKeyPointMax: 300,
  packQuestionMax: 450,
  packExplanationMax: 450,
  packAnswerMax: 240,
  packSubmitNoteMax: 300,
  // v3 practice and tests (C1, C2)
  testRunsPerHour: 5,
  testLinkDays: 7,
  // v3 auditors and audit reports (G1-G6, L10)
  auditorNameMax: 120,
  auditorOrganisationMax: 160,
  auditorPoliciesMax: 200,
  auditorSessionHours: 8,
  auditorCodeTtlMinutes: 15,
  auditorCodeAttempts: 5,
  auditorCodesPerHour: 5,
  /** Wrong code checks over a grant's life before its sign-in locks (R1 #2; an admin's Resend or Change opens it). */
  auditorCodeFailuresMax: 50,
  auditReportLinkDays: 7,
  auditReportPoliciesMax: 1000,
  auditReportPeriodMaxDays: 1096,
  auditReportEmailsMax: 5000,
  auditReportReceiptsMax: 2000,
  /** Reports a workspace may have Queued or Building at once (429 policy_audit_report_busy beyond it). */
  auditReportsBuildingMax: 3,
  /** R1 #7: reports one auditor grant may ask for in 24 hours (429 policy_audit_report_daily_limit {limit} beyond it). */
  auditorReportsPerDay: 10,
} as const

// ── Web-only vocabulary helpers ─────────────────────────────────────────────

/** The Drafts tab's status facet: "All" or one draft state (PolicyDraftPage.statusCounts is keyed by it). */
export type PolicyDraftFilter = 'All' | PolicyDraftStatus

/** Reading counts a page only while this share of it is on screen (DECISIONS L12). */
export const POLICY_READING = { minVisibleRatio: 0.3 } as const

// ── Error codes (mirrors policy-error-codes.ts, backend §9.6) ───────────────

export const POLICY_ERROR_CODES = [
  // Not found
  'policy_not_found',
  'policy_version_not_found',
  'policy_file_not_found',
  'policy_recipient_not_found',
  'policy_task_not_found',
  'policy_evidence_not_found',
  'policy_page_not_found',
  'policy_attestation_not_found',
  'policy_round_not_found',
  'policy_receipt_not_found',
  'policy_key_point_not_found',
  'policy_ai_suggestion_not_found',
  // Library, versions, approval
  'policy_archived',
  'policy_cannot_delete_published',
  'policy_draft_exists',
  'policy_version_number_taken',
  'policy_version_number_invalid',
  'policy_version_number_not_higher',
  'policy_used_by_workflow',
  'policy_used_by_campaign',
  'policy_portal_unavailable',
  'policy_recipient_ineligible',
  'policy_title_locked',
  'policy_version_locked',
  'policy_not_published',
  'policy_version_not_awaiting',
  'policy_version_in_review',
  'policy_nobody_assigned',
  'policy_submit_issues',
  'policy_no_approver',
  'policy_key_points_limit',
  'policy_questions_limit',
  'policy_file_same_as_current',
  'policy_file_no_previous',
  'policy_file_mismatch',
  'policy_cover_key_invalid',
  'policy_compare_same_version',
  'policy_title_required',
  'policy_field_invalid',
  'policy_not_approver',
  'policy_comment_length',
  'policy_question_invalid',
  'policy_owner_invalid',
  'policy_query_invalid',
  'policy_self_approval',
  'policy_withdraw_not_submitter',
  // PDF upload
  'policy_file_not_pdf',
  'policy_file_too_large',
  'policy_file_too_many_pages',
  'policy_file_no_text',
  'policy_file_unreadable',
  // Assign, people actions, reminders
  'policy_audience_invalid',
  'policy_audience_empty',
  'policy_reminders_invalid',
  'policy_due_not_after_today',
  'policy_due_unchanged',
  'policy_due_days_invalid',
  'policy_extend_days_invalid',
  'policy_remove_reason_invalid',
  'policy_selection_empty',
  'policy_attestation_not_live',
  'policy_attestation_not_removed',
  'policy_attestation_not_needs_help',
  'policy_round_closed',
  // Portal, signed link, acknowledgment
  'policy_task_closed',
  'policy_read_first',
  'policy_statement_mismatch',
  'policy_page_too_soon',
  'policy_view_token_invalid',
  'policy_key_points_locked',
  'policy_key_points_first',
  'policy_attempts_exhausted',
  'policy_already_passed',
  'policy_no_questions',
  'policy_attempt_closed',
  'policy_answers_incomplete',
  'policy_answer_invalid',
  'policy_steps_incomplete',
  'policy_sign_in_again',
  'policy_preview_read_only',
  // AI hooks (backend-design §11)
  'policy_ai_disabled',
  'policy_ai_not_set_up',
  'policy_ai_no_credits',
  'policy_ai_suggestion_decided',
  'policy_ai_target_edited',
  // v3 (2026-10-16). The owning package is named on each group; CONTRACT.md lists the routes.
  // Not found (v3 entities)
  'policy_assignment_not_found',
  'policy_exemption_not_found',
  'policy_language_pack_not_found',
  'policy_test_not_found',
  'policy_auditor_not_found',
  'policy_auditor_link_invalid',
  'policy_audit_report_not_found',
  // Settings (P01)
  'policy_settings_invalid',
  'policy_settings_too_few_approvers',
  // Versions, details, archive (P02)
  'policy_document_id_taken',
  'policy_document_id_locked',
  'policy_archive_invalid',
  // As of a past day (P03)
  'policy_as_of_invalid',
  // Sending, compliance, rounds, Remove (P04)
  'policy_assignment_already_sent',
  'policy_assignment_cancelled',
  'policy_assignment_not_cancellable',
  'policy_compliance_already_counts',
  'policy_remove_not_allowed',
  // Exemptions (P04)
  'policy_exemption_exists',
  'policy_exemption_not_requested',
  'policy_exemption_not_approved',
  'policy_exemption_invalid',
  'policy_exemption_withdraw_not_requester',
  // Portal and evidence (P05)
  'policy_display_changed',
  // Language packs (P07)
  'policy_language_not_enabled',
  'policy_language_is_base',
  'policy_language_pack_exists',
  'policy_language_pack_locked',
  'policy_language_pack_outdated',
  'policy_language_pack_not_outdated',
  'policy_language_pack_not_awaiting',
  'policy_language_pack_issues',
  'policy_language_file_pages_mismatch',
  'policy_language_no_published_pack',
  // Practice: preview and "send a test to myself" (P08)
  'policy_test_rate_limited',
  'policy_test_not_ready',
  'policy_test_no_email',
  'policy_test_expired',
  'policy_test_sender_no_access',
  'policy_test_outdated',
  // External auditors and audit reports (P09)
  'policy_auditor_exists',
  'policy_auditor_invalid',
  'policy_auditor_revoked',
  'policy_auditor_code_invalid',
  'policy_auditor_code_expired',
  'policy_auditor_rate_limited',
  'policy_auditor_session_expired',
  'policy_auditor_access_ended',
  'policy_auditor_locked',
  'policy_auditor_link_unavailable',
  'policy_audit_report_invalid',
  'policy_audit_report_not_ready',
  'policy_audit_report_expired',
  'policy_audit_report_busy',
  'policy_audit_report_delete_not_allowed',
  'policy_audit_report_daily_limit',
] as const
export type PolicyErrorCode = (typeof POLICY_ERROR_CODES)[number]

export function isPolicyErrorCode(value: unknown): value is PolicyErrorCode {
  return typeof value === 'string' && (POLICY_ERROR_CODES as readonly string[]).includes(value)
}

// ── Issue codes (mirrors policy-issue-codes.ts, backend §9.7, in the handover's order) ─

export const POLICY_ISSUE_CODES = [
  // What
  'file_missing',
  'title_missing',
  'document_id_missing',
  'effective_date_missing',
  'change_note_missing',
  'revision_rule_change',
  // Confirm
  'key_points_too_few',
  'key_point_empty',
  'key_point_page_missing',
  'revision_promises_changed',
  // Ask (per question, the first failing check only)
  'question_text_missing',
  'question_answer_empty',
  'question_correct_missing',
  'question_one_correct',
  'question_all_correct',
  'question_duplicate_answers',
  'question_page_missing',
] as const
export type PolicyIssueCode = (typeof POLICY_ISSUE_CODES)[number]

export function isPolicyIssueCode(value: unknown): value is PolicyIssueCode {
  return typeof value === 'string' && (POLICY_ISSUE_CODES as readonly string[]).includes(value)
}

/** v3: a language pack's "things to fix" (mirrors POLICY_PACK_ISSUE_CODES in policy-issue-codes.ts). */
export const POLICY_PACK_ISSUE_CODES = [
  'pack_item_missing',
  'pack_item_empty',
  'pack_item_unchecked',
  'pack_answers_mismatch',
] as const
export type PolicyPackIssueCode = (typeof POLICY_PACK_ISSUE_CODES)[number]

export function isPolicyPackIssueCode(value: unknown): value is PolicyPackIssueCode {
  return typeof value === 'string' && (POLICY_PACK_ISSUE_CODES as readonly string[]).includes(value)
}

// ═════════════════════════════════════════════════════════════════════════════
// RESPONSES — a verbatim copy of policy-responses.types.ts (the guard reads it)
// ═════════════════════════════════════════════════════════════════════════════

// ── Shared pieces ───────────────────────────────────────────────────────────

/** A JSON value (event parameters, audit detail parameters). */
export type PolicyJsonValue = string | number | boolean | null | PolicyJsonValue[] | { [key: string]: PolicyJsonValue }

/** Structured parameters of an event or a log row; the web words them through i18n. */
export type PolicyParams = Record<string, PolicyJsonValue>

/** A person by id and display name (an owner, a submitter, an approver). */
export interface PolicyUserRef {
  id: string
  name: string
}

/** A person by display name only (who approved, who sent back). */
export interface PolicyNameRef {
  name: string
}

/** The `buildPaginatedResult` envelope plus the workspace zone the page's instants were counted in. */
export interface PolicyPage<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
  unfilteredTotal: number
  timeZone: string
}

/**
 * ONE definition of every number (backend-design §6.2). The library row, the
 * policy page, the people facets, the dashboard, the CSVs, vCRO and the portal
 * ring all read the same SQL, so the same number never differs between screens.
 * Over the selected round's rows; `assigned` = eligible people on a live row.
 */
export interface PolicyFigures {
  assigned: number
  opened: number
  read: number
  confirmed: number
  passed: number
  acknowledged: number
  /** v3 (F2): acknowledged / assigned, the Acknowledged column ("95% · 118 of 124"); null when assigned is 0. */
  acknowledgedRate: number | null
  covered: number
  /**
   * v3 (L1): IN GOOD STANDING = complianceAssigned - complianceOverdue, over the COMPLIANCE rows (the compliance CTE:
   * each eligible person's counting row in the rounds that count, D8 / L2), not over the round's tracking rows.
   */
  compliant: number
  /** compliant / complianceAssigned; null when complianceAssigned is 0 or nothing counts yet (complianceCounts false). */
  complianceRate: number | null
  /** v3 (L1): the people the compliance figure is over (eligible, on a counting row). */
  complianceAssigned: number
  /** v3 (L1): of those, the people past their due day without acknowledging a counting version. */
  complianceOverdue: number
  /** v3 (L2): false while no round of the policy counts toward compliance (policies.compliance_round = 0). */
  complianceCounts: boolean
  notStarted: number
  inProgress: number
  needsHelp: number
  overdue: number
  overdueRate: number | null
  /** Assigned people whose due day has passed (the on-time denominator). */
  dueBase: number
  onTime: number
  onTimeRate: number | null
  removed: number
  /** v3 (E3): rows Exempted (they are in no other figure). */
  exempted: number
  /** People who left the company (not counted anywhere). */
  left: number
  superseded: number
  archivedBeforeAcknowledgment: number
}

// ── Library (P5) ────────────────────────────────────────────────────────────

/**
 * GET /library/inbox. v3: language packs wait and come back too (kind 'Language Pack', packId and language set).
 * `selfApproval` on a waiting item: the caller submitted it and named themselves (the review page shows the
 * "You made this … and chose to approve it yourself" banner).
 */
export interface PolicyInbox {
  waitingForMe: Array<{
    kind: PolicyDraftKind
    policyId: string
    versionId: string
    packId: string | null
    language: string | null
    title: string
    versionLabel: string
    /** Null once the submitter's account is deleted (submitted_by_id is ON DELETE SET NULL). */
    submittedBy: PolicyUserRef | null
    submittedAt: string
    selfApproval: boolean
  }>
  sentBackToMe: Array<{
    kind: PolicyDraftKind
    policyId: string
    versionId: string
    packId: string | null
    language: string | null
    title: string
    versionLabel: string
    /** Null once the reviewer's account is deleted (reviewed_by_id is ON DELETE SET NULL). */
    reviewedBy: PolicyNameRef | null
    comment: string
  }>
  /** v3: exemption requests the caller may decide (create:PolicyApproval; never their own exemption). */
  exemptionsToDecide: number
  counts: { published: number; drafts: number; archived: number }
}

/** GET /approvals/count — the sidebar badge. v3: waitingForMe = versions + packs + exemptions. */
export interface PolicyApprovalsCount {
  waitingForMe: number
  versions: number
  packs: number
  exemptions: number
}

/** A version draft as the library and the policy page describe it. */
export interface PolicyVersionDraftRef {
  versionId: string
  label: string
  status: PolicyDraftStatus
}

/** Per-row permission answers: the UI never re-derives "am I the submitter". */
export interface PolicyLibraryRowCan {
  assign: boolean
  remind: boolean
  newVersion: boolean
  archive: boolean
}

/** GET /library?section=published — one row. */
export interface PolicyLibraryRow {
  id: string
  title: string
  /** v3 (A1): the maker's document ID (dir="ltr"); null only for a policy published before v3 (none exist). */
  documentId: string | null
  category: PolicyCategory | null
  coverKey: PolicyCoverKey | null
  pageCount: number
  owner: PolicyUserRef | null
  /** v3 (F2): no owner, or the owner can no longer make or approve policies ("Owner needed"; computed in SQL). */
  ownerNeeded: boolean
  currentVersion: { id: string; label: string }
  versionDraft: PolicyVersionDraftRef | null
  /** v3 (F2): the language chips, base first (one grouped query over policy_language_packs). */
  languages: PolicyLanguageChip[]
  assignedEver: boolean
  figures: PolicyFigures
  /** The latest due day across the current round's assignments. */
  dueOn: string | null
  /** Pending people not emailed or reminded today (the "Remind <n> pending" count). */
  remindablePendingToday: number
  can: PolicyLibraryRowCan
}

/** GET /library?section=drafts — one row (v3: a version, or a language pack of a policy). */
export interface PolicyDraftRow {
  kind: PolicyDraftKind
  policyId: string
  versionId: string
  /** Language Pack rows: the pack and its language; null for a Version row. */
  packId: string | null
  language: string | null
  title: string
  documentId: string | null
  isNewVersion: boolean
  parentVersionLabel: string | null
  changeNote: string | null
  pageCount: number | null
  hasFile: boolean
  versionLabel: string
  status: PolicyDraftStatus
  preparedBy: PolicyUserRef | null
  /** Awaiting Approval: the named approver; Changes Requested: who sent it back. */
  approver: PolicyUserRef | null
  updatedAt: string
  /** Review = the caller is the named approver (v3: also when they are the submitter and self-approval is allowed). */
  viewerAction: PolicyDraftViewerAction
}

/** GET /library?section=archived — one row. */
export interface PolicyArchivedRow {
  id: string
  title: string
  documentId: string | null
  category: PolicyCategory | null
  lastVersionLabel: string
  archivedAt: string
  archivedBy: PolicyNameRef | null
  /** v3 (L4): the Reason column; null only for a policy archived before v3 (none exist). */
  archiveReason: PolicyArchiveReason | null
  archiveNote: string | null
  replacedBy: { id: string; title: string } | null
  /** People in the last round who were not removed. */
  recipients: number
  acknowledged: number
}

export type PolicyLibraryPage = PolicyPage<PolicyLibraryRow>

export interface PolicyDraftPage extends PolicyPage<PolicyDraftRow> {
  statusCounts: Record<'All' | PolicyDraftStatus, number>
}

export type PolicyArchivedPage = PolicyPage<PolicyArchivedRow>

/** GET /library?section= answers one of the three, by the section asked for. */
export type PolicyLibraryListing = PolicyLibraryPage | PolicyDraftPage | PolicyArchivedPage

/** POST /library and POST /library/:id/clone — the new untitled (or "(copy)") 1.0 draft. */
export interface PolicyCreated {
  policyId: string
  versionId: string
}

/** The round list of a policy: each round labelled with its version (the API never says "round" in text). */
export interface PolicyRoundRef {
  round: number
  versionId: string
  versionLabel: string
  reason: PolicyRoundReason
  openedAt: string
  dueOn: string | null
  current: boolean
  /** v3 (E2): the acknowledgment cycle (C{cycle} in the receipt number): 1 at a publish, +1 per Ask Again. */
  cycle: number
  /** v3 (L2): the round's compliance mode, from its opener (a round without an opener counts). */
  countsTowardCompliance: boolean
  /** When an approver made an acknowledgment-only round count ("Make it count"); null otherwise. */
  complianceChangedAt: string | null
  /** Ask Again rounds: who was asked, the note shown to them, and who asked. Null on other rounds. */
  reackWho: PolicyReackWho | null
  requestNote: string | null
  askedBy: PolicyNameRef | null
}

/**
 * GET /library/:id (R7). Only a policy that has a version in force: a never-published policy (status Draft) is
 * 409 policy_not_published, so `status` is Published or Archived and `currentVersion` is never null. An archived
 * policy shows the version that was in force when it was archived.
 */
export interface PolicyDetail {
  id: string
  title: string
  /** v3 (A1, L15): the maker's document ID, the receipt prefix (replaces v1's refCode). */
  documentId: string | null
  category: PolicyCategory | null
  coverKey: PolicyCoverKey | null
  owner: { id: string; name: string; email: string | null } | null
  /** v3 (F2): no owner, or the owner can no longer make or approve policies. */
  ownerNeeded: boolean
  status: 'Published' | 'Archived'
  firstPublishedAt: string | null
  archivedAt: string | null
  archivedBy: PolicyNameRef | null
  /** v3 (L4). */
  archiveReason: PolicyArchiveReason | null
  archiveNote: string | null
  replacedBy: { id: string; title: string } | null
  currentVersion: {
    id: string
    label: string
    /** v3 (E1): 1 for a new version; 2+ for a Revision ("1.0 rev 2" is the label). */
    revision: number
    changeType: PolicyChangeType
    effectiveOn: string | null
    reviewBy: string | null
    /** v3 (L5): review_by is today or past (the Version card's "Due" chip; a dashboard item). */
    reviewDue: boolean
    publishedAt: string | null
    approvedBy: PolicyNameRef | null
    submittedBy: PolicyNameRef | null
    /** v3 (D5): the approver approved their own submission. */
    selfApproved: boolean
    /** v3 (A1): who approved it outside HumanFirewall ("Board of Directors") and on which day; both null when not given. */
    managementApproval: string | null
    managementApprovedOn: string | null
    /** v3 (A1, L11): framework control references ("A.5.10"), at most POLICY_LIMITS.controlTagsMax. */
    controlTags: string[]
    /** v3 (D8): false for a Material version released acknowledgment-only. */
    countsTowardCompliance: boolean
    /** v3 (L12): the reason the maker kept a Revision despite the PDF-text check; null otherwise. */
    revisionOverrideReason: string | null
    pageCount: number
    file: { name: string; sizeBytes: number; sha256: string }
    keyPointCount: number
    questionCount: number
    passMark: number
    attemptsAllowed: number
  }
  /** The latest due day across the current round's assignments. */
  dueOn: string | null
  versionDraft: (PolicyVersionDraftRef & { approver: PolicyNameRef | null; submittedBy: PolicyNameRef | null }) | null
  rounds: PolicyRoundRef[]
  assignedEver: boolean
  /** The re-acknowledgment window of the current round: who still counts as covered until when. */
  reackWindow: { dueOn: string; coveredCount: number } | null
  /** `sampleNames` is [] unless the caller holds read:Recipient (actor.abilities.readRecipient); the count stays. */
  lateJoiners: { count: number; sampleNames: string[] }
  /** v3 (D1): Admin assignments waiting out their cool-off (the "assignment emails go out at …" banner); oldest first. */
  sending: PolicySendingAssignment[]
  /** v3 (A8, F3): the base language, then each pack language, as the Languages card and library chips show them. */
  languages: PolicyLanguageChip[]
  /** v3 (D6): policies.base_language and display_language ("What do employees see?"; null = base only). */
  baseLanguage: string
  displayLanguage: string | null
  /** v3 (L1): the version label the compliance-overdue people are still pending on, when it is not the current one ("6 overdue on v1.0"). */
  complianceOverdueOnVersionLabel: string | null
  /** v3 (L5): the latest "Mark as reviewed" of the policy. */
  lastReview: PolicyReviewRow | null
  /** v3 (E3): exemption requests on this policy (or All Policies) the caller may decide now. */
  exemptionsToDecide: number
  /** v3 (L8): the workspace's evidence retention in years (the Details drawer and About print it). */
  retentionYears: number
  can: {
    assign: boolean
    remind: boolean
    changeDue: boolean
    archive: boolean
    newVersion: boolean
    export: boolean
    /** v3: Update (New version or Revision; on an archived policy a New version only, L4). */
    update: boolean
    /** v3 (L11): edit owner, category, review-by, control tags (update:Policy). */
    editDetails: boolean
    /** v3 (L5): Mark as reviewed (update:Policy). */
    markReviewed: boolean
    /** v3 (E2): Ask everyone to re-acknowledge (manage:PolicyAssignment, a live round). */
    askAgain: boolean
    /** v3 (E3): request an exemption (manage:PolicyAssignment). */
    requestExemption: boolean
    /** v3 (E3): decide or end exemptions (create:PolicyApproval). */
    decideExemptions: boolean
    /** v3 (D3): make an acknowledgment-only round count (create:PolicyApproval). */
    makeItCount: boolean
    /** v3 (C1, L16): preview as employee and send a test to myself (read:Policy). */
    preview: boolean
    sendTest: boolean
    /** v3 (A3, L17): add a language and change what employees see (update:Policy). */
    manageLanguages: boolean
  }
  timeZone: string
  today: string
}

/** GET /library/:id/archive/preview — the archive dialog's numbers. */
export interface PolicyArchivePreview {
  notAcknowledged: number
  acknowledged: number
  draft: { label: string; state: PolicyDraftStatus } | null
  /** 'Awaiting Approval' while a submission blocks the archive (409 policy_version_in_review). */
  blockedBy: 'Awaiting Approval' | null
  /** v3 (D1): waiting Admin assignments the archive cancels (nobody in them is assigned). */
  waitingAssignments: number
  /** v3 (E3): approved or requested exemptions scoped to this policy that end with the archive. */
  liveExemptions: number
  /**
   * C8: what still REQUIRES the policy, from the two reads the archive refuses on (409 policy_used_by_workflow when an active
   * workflow names it, 409 policy_used_by_campaign when a phishing campaign that has not finished does): the dialog names them and
   * turns Archive off instead of letting the person fill in the form and be refused.
   */
  usedBy: { workflows: Array<{ id: string; name: string }>; campaigns: Array<{ id: string; name: string }> }
}

/** POST /library/:id/archive. */
export interface PolicyArchiveResult {
  notAcknowledged: number
  acknowledged: number
  draftDiscarded: boolean
  /** v3 (D1). */
  assignmentsCancelled: number
  /** v3 (E3): exemptions scoped to this policy, ended. */
  exemptionsEnded: number
  archiveReason: PolicyArchiveReason
}

/** GET /roles — who can do what (derived from effective grants, as JwtStrategy resolves them). */
export interface PolicyRoleRow {
  userId: string
  name: string
  email: string
  jobTitle: string | null
  canSubmit: boolean
  canApprove: boolean
  canAssign: boolean
  canViewEvidence: boolean
  /** v3: update:PolicySettings. */
  canManageSettings: boolean
  /** v3: create:PolicyAuditAccess (invite external auditors, build audit reports). */
  canManageAuditors: boolean
}

/**
 * GET /approvers?excludeSelf=true — canApprove members. v3 (D5): the caller is listed (isSelf true, first) only when
 * the workspace allows self-approval and excludeSelf is not set.
 */
export interface PolicyApproverRow {
  userId: string
  name: string
  jobTitle: string | null
  isSelf: boolean
}

/** GET /options/remedial — the policies a workflow or a phishing campaign may ask someone to acknowledge. */
export interface PolicyRemedialOption {
  id: string
  title: string
  versionLabel: string
}

// ── Versions, content, approval (P5, P3) ────────────────────────────────────

export interface PolicyKeyPoint {
  id: string
  position: number
  text: string
  citedPage: number
  /** The previous version's item (drives New / Changed / Removed). */
  copiedFromId: string | null
  aiSuggestionId: string | null
  /** Set when a person edits the text or page; AI never overwrites an item edited after its suggestion. */
  makerEditedAt: string | null
}

export interface PolicyQuestionAnswer {
  id: string
  text: string
  correct: boolean
  /** True/false answers are fixed; the portal translates them. */
  fixed: PolicyFixedAnswer | null
  /**
   * v3 (H5): an "All of the above" / "None of the above" answer, detected AT SAVE (positionDependentAnswer): the shuffle
   * keeps it last, in authored order. The maker never sets it.
   */
  last: boolean
}

export interface PolicyQuestion {
  id: string
  position: number
  type: PolicyQuestionType
  text: string
  citedPage: number
  explanation: string
  answers: PolicyQuestionAnswer[]
  copiedFromId: string | null
  aiSuggestionId: string | null
  makerEditedAt: string | null
}

/** One entry of the "things to fix before submitting" list. */
export interface PolicyIssue {
  step: PolicyIssueStep
  code: PolicyIssueCode
  params: {
    index: number | null
    page: number | null
    pageCount: number | null
  }
}

export interface PolicyVersionPageRef {
  pageNo: number
  title: string
  changed: boolean
}

/** GET /library/:id/versions/:versionId. */
export interface PolicyVersionDetail {
  policyId: string
  versionId: string
  /** "1.0", or "1.0 rev 2" for a Revision (the helper P02 exports; the web words "rev" through i18n). */
  label: string
  major: number
  minor: number
  /** v3 (E1): 1 for a new version; 2+ for a Revision of the same major.minor. Never typed by the maker. */
  revision: number
  status: PolicyVersionStatus
  changeType: PolicyChangeType
  /** A version of a published policy (not the first issue). */
  isNewVersion: boolean
  /** The version this one was drafted from. */
  parent: { versionId: string; label: string; fileSha256: string } | null
  title: string
  /** v3 (A1): the policy's document ID (editable here only while the policy is a Draft). */
  documentId: string | null
  category: PolicyCategory | null
  owner: PolicyUserRef | null
  effectiveOn: string | null
  reviewBy: string | null
  summary: string
  changeNote: string
  /** One of POLICY_REACK_DAYS on a Material version, else null. */
  reackDays: number | null
  /** v3 (A1): management approval outside HumanFirewall, and its day. */
  managementApproval: string | null
  managementApprovedOn: string | null
  /** v3 (A1, L11). */
  controlTags: string[]
  /** v3 (D8): "When it's published" — false = acknowledgment only (Material only). */
  countsTowardCompliance: boolean
  /** v3 (L12): the maker's reason for keeping a Revision the PDF-text check flagged. */
  revisionOverrideReason: string | null
  /** v3 (D5): set when the approver approved their own submission. */
  selfApproved: boolean
  file: { name: string; sizeBytes: number; sha256: string; pageCount: number } | null
  /** The file "Replace File" can bring back. */
  previousFile: { name: string } | null
  pages: PolicyVersionPageRef[]
  changedPages: number[]
  removedPageCount: number
  /** Which fields the PDF's document-control table filled. */
  prefilled: PolicyPrefillField[]
  /** v3 (A1, L13): which fields the "Fill with AI" Details run filled (AI provenance chips). */
  prefilledByAi: PolicyPrefillField[]
  keyPoints: PolicyKeyPoint[]
  questions: PolicyQuestion[]
  passMark: number
  attemptsAllowed: number
  approval: {
    status: PolicyVersionStatus
    submittedBy: PolicyUserRef | null
    submittedAt: string | null
    approver: PolicyUserRef | null
    note: string | null
    reviewedBy: PolicyNameRef | null
    reviewedAt: string | null
    comment: string | null
  }
  issues: PolicyIssue[]
  locked: boolean
  can: {
    edit: boolean
    submit: boolean
    withdraw: boolean
    delete: boolean
    approve: boolean
    requestChanges: boolean
  }
}

/** GET /library/:id/versions — one row of the Versions tab. */
export interface PolicyVersionRow {
  versionId: string
  label: string
  /** v3 (E1). */
  revision: number
  status: PolicyVersionStatus
  publishedAt: string | null
  changeType: PolicyChangeType
  changeNote: string | null
  changedPages: number[]
  approvedBy: PolicyNameRef | null
  submittedBy: PolicyNameRef | null
  /** v3 (D5). */
  selfApproved: boolean
  /** v3 (D8). */
  countsTowardCompliance: boolean
  /** Awaiting Approval: who it is waiting for. */
  waitingFor: PolicyNameRef | null
  file: { name: string; sha256: string; sizeBytes: number } | null
  /** Proofs of this version: COUNT(*) FROM policy_acknowledgment_evidence WHERE version_id. */
  acknowledgedCount: number
  round: number | null
}

/** POST /library/:id/versions (a new version or a Revision of a published policy; a new version of an archived one). */
export interface PolicyVersionCreated {
  versionId: string
  label: string
  /** v3 (E1): 1 for a new version; the current revision + 1 for a Revision. */
  revision: number
  changeType: PolicyChangeType
  /** Material: POLICY_LIMITS.reackDaysDefault until changed; null for a Revision. */
  reackDays: number | null
  effectiveOn: string
}

/** What a PDF upload found (W15). */
export interface PolicyFileUploadSummary {
  fileName: string
  sizeBytes: number
  pageCount: number
  sha256: string
  prefilled: PolicyPrefillField[]
  changedPages: number[]
  removedPageCount: number
}

/** PUT …/file. */
export interface PolicyFileUploadResult {
  version: PolicyVersionDetail
  upload: PolicyFileUploadSummary
}

/** GET …/pages — the page list (no text). */
export interface PolicyPageSummary {
  pageNo: number
  title: string
  charCount: number
  changed: boolean
}

/** GET …/pages/:pageNo — one page's extracted text (screen readers, AI, the page viewer). */
export interface PolicyPageText {
  pageNo: number
  title: string
  text: string
  changed: boolean
}

/** An item in a version comparison. */
export interface PolicyDiffKeyPoint {
  id: string | null
  text: string
  citedPage: number
  change: PolicyDiffChange
}

export interface PolicyDiffQuestion {
  id: string | null
  type: PolicyQuestionType
  text: string
  citedPage: number
  explanation: string
  answers: Array<{ text: string; correct: boolean }>
  change: PolicyDiffChange
}

/** GET …/compare and the review page's diff: matched by `copied_from_id`. */
export interface PolicyContentDiff {
  changedPages: number[]
  removedPageCount: number
  keyPoints: PolicyDiffKeyPoint[]
  questions: PolicyDiffQuestion[]
}

export interface PolicyReviewCheck {
  key: PolicyReviewCheckKey
  ok: boolean
  params: PolicyParams
}

/** What approving a Material version does to people (null for a first issue or a Minor version). */
export interface PolicyReackPlan {
  recipients: number
  acknowledgedStayCompliant: number
  unfinishedSuperseded: number
  dueOn: string
  /** v3 (D8): the version is released acknowledgment-only when false (the approve dialog words it so). */
  countsTowardCompliance: boolean
}

/** GET …/review — the version detail plus what the approver needs to decide. */
export interface PolicyVersionReview extends PolicyVersionDetail {
  checks: PolicyReviewCheck[]
  reackPlan: PolicyReackPlan | null
  /** People who acknowledged the version in force (a Minor approval keeps them all). */
  currentAcknowledgedCount: number
  diff: PolicyContentDiff
  /** v3 (A6): the pack languages that will need updating once this version is published. */
  packsOutdatedOnPublish: string[]
  /**
   * v3 (D5): `selfApproval` = the caller submitted it, named themselves, and the workspace allows it (the review page's
   * "You made this policy and chose to approve it yourself" banner); canApprove is then true.
   */
  viewer: { canApprove: boolean; canWithdraw: boolean; isSubmitter: boolean; isNamedApprover: boolean; selfApproval: boolean; canRequestChanges: boolean }
}

export interface PolicySubmitResult {
  status: PolicyVersionStatus
  submittedAt: string
}

export interface PolicyWithdrawResult {
  status: PolicyVersionStatus
}

export interface PolicyRequestChangesResult {
  status: PolicyVersionStatus
}

export interface PolicyApproveResult {
  policyId: string
  versionId: string
  label: string
  changeType: PolicyChangeType
  /** v3 (D5). */
  selfApproved: boolean
  /**
   * Material only: who must acknowledge again, by when. `emailSwitchedOff`: people were to be mailed, but
   * Settings -> Emails has the "New version" email OFF, so no mail was queued. v3: `countsTowardCompliance` false =
   * released acknowledgment-only.
   */
  reacknowledge: { recipients: number; dueOn: string; emailSwitchedOff: boolean; countsTowardCompliance: boolean } | null
  /** v3 (D1): waiting Admin assignments released first, as the publish requires. */
  releasedAssignments: number
  /** v3 (A6): pack languages that now need updating. */
  packsNeedingUpdate: string[]
}

// ── Policy page: figures, people, assign (P4, P6) ───────────────────────────

/** GET /library/:id/progress?round= . */
export interface PolicyProgress extends PolicyFigures {
  /** Rows a "Remind <n> pending" would reach. */
  remindable: number
  /** … of which not already mailed or reminded today. */
  remindableNotToday: number
  hasQuestions: boolean
  timeZone: string
  today: string
}

export interface PolicyRecipientProgress {
  /** 1 Read, 2 Key points, 3 Questions, 4 Passed (not yet acknowledged), 5 Acknowledged. */
  step: 1 | 2 | 3 | 4 | 5
  pagesCounted: number
  pageCount: number
  keyPointsTicked: number
  keyPointCount: number
  attemptsUsed: number
  attemptsAllowed: number
  bestScore: number | null
}

/** GET /library/:id/people — one person on one policy in one round. */
export interface PolicyRecipientRow {
  attestationId: string
  recipientId: string
  name: string
  email: string
  department: string
  /** false = left the company (eligible recipients only count). */
  active: boolean
  status: PolicyAttestationStatus
  overdue: boolean
  covered: boolean
  /** The due day of the round that covers them while the new version is not yet due. */
  coveredUntil: string | null
  dueOn: string
  originalDueOn: string
  progress: PolicyRecipientProgress
  openedAt: string | null
  acknowledgedAt: string | null
  acknowledgedVersionLabel: string | null
  score: number | null
  remindersSent: number
  lastRemindedAt: string | null
  /** Emailed or reminded today (workspace day): the daily job and Remind skip them. */
  mailedToday: boolean
  removal: { reason: PolicyRemovalReason; note: string | null; by: PolicyNameRef | null; at: string } | null
  supersededByVersionLabel: string | null
  /** v3 (E2): the row's acknowledgment cycle. */
  cycle: number
  /** v3 (L2): whether the row's round counts toward compliance. */
  countsTowardCompliance: boolean
  /** v3 (E3): the live or latest exemption covering this person on this policy (scope This Policy or All Policies). */
  exemption: PolicyExemptionRef | null
  /**
   * The receipt reference as text (v3: EV-IV-ISMS-POL-012-0001-v1.0-C1), shown for unfinished rows too. NOT an id: the
   * acknowledgment id the receipt routes take as :acknowledgmentId is `acknowledgment.evidenceId` of the evidence
   * record / portal receipt.
   */
  evidenceRef: string
  can: {
    remind: boolean
    resetAttempts: boolean
    /** v3 (L7): only within POLICY_LIMITS.removeWindowDays of the assignment and before the person opens it. */
    remove: boolean
    restore: boolean
    select: boolean
    /** v3 (E3): request an exemption for this person (manage:PolicyAssignment; none live already). */
    requestExemption: boolean
  }
}

export interface PolicyPeoplePage extends PolicyPage<PolicyRecipientRow> {
  statusCounts: Record<PolicyPeopleStatusFilter, number>
  departments: string[]
}

/** One event of a person's timeline (the evidence page). */
export interface PolicyEvidenceEvent {
  seq: number
  at: string
  kind: PolicyEventKind
  actor: PolicyEventActor
  actorName: string
  params: PolicyParams
  /** 'Chrome · macOS'; only on Opened and Acknowledged. */
  device: string | null
  /** The raw address: only with read:PolicyEvidence. */
  ip: string | null
}

/** The receipt as a person (or an auditor) reads it. */
export interface PortalPolicyReceipt {
  /** The acknowledgment's uuid: what the receipt routes take as :acknowledgmentId. The EV-... text is `reference`. */
  evidenceId: string
  /** v3 (L15): EV-{documentId}-{personSeq4}-v{major.minor}-C{cycle}, e.g. EV-IV-ISMS-POL-012-0001-v1.0-C1. */
  reference: string
  /** v3: the policy's document ID at acknowledgment. */
  documentId: string
  /** v3 (E2): the acknowledgment cycle (the C in the reference). */
  cycle: number
  orgName: string
  timeZone: string
  /** v3: the UTC offset of acknowledgedAt in that zone, as printed: "UTC+5:30" (never a zone abbreviation). */
  utcOffset: string
  retentionYears: number
  employee: { name: string; email: string; department: string }
  policy: { title: string; category: PolicyCategory | null; owner: string | null }
  versionLabel: string
  currentVersionLabel: string
  /** False when the policy has since moved to a newer version ("This receipt is for v1.0 …"). */
  isCurrent: boolean
  currentIsMinorUpdate: boolean
  coveredUntil: string | null
  archivedOn: string | null
  effectiveOn: string
  /** The BASE file (the pages were counted against it). */
  file: { name: string; pageCount: number; sha256: string }
  /** v3 (H6): the pack's own PDF when one was shown beside the base file; null otherwise. */
  secondFile: { language: string; name: string; pageCount: number; sha256: string } | null
  /** The base-language statement (kept for v1 readers; it is statements[0]). */
  statement: { text: string; language: PolicyLanguage }
  /** v3 (D6, H6): every statement acknowledged, base first, then the pack's; each with its direction and fingerprint. */
  statements: PolicyStatementShown[]
  /** v3 (D6): the languages shown, base first ('en', 'ar'). */
  shownLanguages: string[]
  /** v3: the pack's translated title when a pack was shown; null otherwise. */
  packTitle: string | null
  /** v3 (H3): each page was on screen for at least this many seconds. */
  pageMinSeconds: number
  acknowledgedAt: string
  dueOn: string
  daysLate: number
  device: { browser: string; os: string } | null
  ip: string | null
  identificationMethod: PolicyIdentificationMethod
  pagesRead: number
  pagesTotal: number
  questionnaire: { score: number; attempt: number; attempts: number; passMark: number } | null
  keyPoints: string[]
  /** v3 (H7): the proof row's hash (row_hash), shown as the evidence fingerprint. */
  evidenceHash: string
}

/** GET /library/:id/people/:attestationId — one person's evidence. */
export interface PolicyEvidenceRecord {
  /**
   * The receipt reference as text (EV-IV-ISMS-POL-012-0001-v1.0-C1); present for an unfinished row too. NOT an id: the
   * acknowledgment id for the receipt routes is `acknowledgment.evidenceId` (null until the person acknowledges).
   */
  evidenceRef: string
  person: { name: string; email: string; department: string; active: boolean }
  policy: { id: string; title: string }
  version: { label: string; effectiveOn: string; isCurrent: boolean; currentLabel: string }
  status: PolicyAttestationStatus
  dueOn: string
  daysLate: number | null
  acknowledgment: PortalPolicyReceipt | null
  pagesRead: number
  pageCount: number
  keyPointsConfirmed: number
  keyPointCount: number
  questionnaire: { passed: boolean; best: number | null; attemptsUsed: number; attemptsAllowed: number; passMark: number } | null
  statementText: string | null
  file: { name: string; pageCount: number; sha256: string }
  /** v3: the row's cycle, reading minimum (null until opened) and the languages it was shown in (base first). */
  cycle: number
  pageMinSeconds: number | null
  shownLanguages: string[]
  /** v3 (E3): the exemption on the row, if any. */
  exemption: PolicyExemptionRef | null
  events: PolicyEvidenceEvent[]
  chain: { timelineOk: boolean; headHash: string | null }
  timeZone: string
}

/** GET /evidence/verify — the proof chain, every timeline chain, and each proof's timeline head. */
export interface PolicyEvidenceVerification {
  proofChain: { rows: number; ok: boolean; firstBrokenSeq: number | null; headSeq: number | null; headHash: string | null }
  timelines: { rows: number; ok: boolean; firstBroken: { attestationId: string; seq: number } | null }
  heads: { ok: boolean; mismatches: number }
}

export interface PolicyReminderSwitches {
  afterAssign3: boolean
  afterAssign7: boolean
  onDue: boolean
  overdue3: boolean
}

/** GET /library/:id/reminders — `mixed` when the round's batches differ. */
export interface PolicyReminderSettings extends PolicyReminderSwitches {
  mixed: boolean
}

/** GET /library/:id/assignments — one assign action (Assignment 1, 2 …). */
export interface PolicyAssignmentRow {
  id: string
  seqNo: number
  origin: PolicyAssignmentOrigin
  createdAt: string
  createdBy: PolicyNameRef | null
  assignedCount: number
  skippedCount: number
  dueOn: string
  /** "All Recipients · 2 individuals". */
  label: string
  reminders: PolicyReminderSwitches
  /** v3 (D1): Waiting (cool-off), Sent (released) or Cancelled; every non-Admin origin is Sent. */
  sendState: PolicySendState
  /** When its emails go out (Waiting) or went out; the assignment's send_after. */
  sendAfter: string
  releasedAt: string | null
  cancelledAt: string | null
  cancelledBy: PolicyNameRef | null
  /** v3 (D1): people the click picked who wait in the queue until release (0 once released or cancelled). */
  queuedCount: number
  /** v3 (L2): this assignment opened its round (and so set the round's compliance mode). */
  opensRound: boolean
  /** v3 (L2): the round's mode as this assignment inherited or set it. */
  countsTowardCompliance: boolean
  /** v3 (E2): the cycle of its round. */
  cycle: number
  /** v3 (E2): Ask Again only. */
  reackWho: PolicyReackWho | null
  requestNote: string | null
}

/** The warnings an assign preview and an assign carry (not refusals). */
export interface PolicyAssignWarnings {
  dueBeforeEffective: boolean
}

/** POST /library/:id/assignments/preview. */
export interface PolicyAudiencePreview {
  selected: number
  new: number
  /** Including removed people: a removed person still owns the slot. v3: and people waiting in a queued assignment. */
  alreadyAssigned: number
  /** v3 (D1): of alreadyAssigned, the people waiting in a queued (unreleased) assignment. */
  queued: number
  /** v3 (E3): of the selection, people exempted from this policy (they are skipped). */
  exempted: number
  inMoreThanOneGroup: number
  bulkPasteMatched: number
  bulkPasteUnknown: string[]
  bulkPasteUnknownCount: number
  groupAlreadyAssigned: Record<string, number>
  recipientAssigned: Record<string, boolean>
  dueOn: string
  schedule: Array<{ trigger: PolicyReminderTrigger; on: string; skipped: boolean }>
  warnings: PolicyAssignWarnings
  /**
   * v3 (D1): when the emails would go out with the cool-off on (now + the workspace's cool_off_minutes; the delivery
   * window may push it later: windowOpensAt), and the workspace defaults the page starts from.
   */
  sendAfter: string
  windowOpensAt: string | null
  coolOffDefault: boolean
  coolOffMinutes: number
  /**
   * v3 (L2): true when this assignment would OPEN the round (the Assign page offers "Counts toward compliance" /
   * "Acknowledgment only", starting at defaultComplianceMode); false when it joins a round whose mode it inherits
   * (roundCountsTowardCompliance).
   */
  complianceChoice: boolean
  roundCountsTowardCompliance: boolean | null
  defaultComplianceMode: PolicyComplianceMode
}

/** POST /library/:id/assignments. */
export interface PolicyAssignResult {
  assignmentId: string
  seqNo: number
  /** With the cool-off: 0 until release (the people wait in the queue: `queued`). */
  assigned: number
  skippedAlreadyAssigned: number
  dueOn: string
  warnings: PolicyAssignWarnings
  /** The assignment email is switched OFF in Settings -> Emails: the people were assigned, and no mail was queued. */
  emailSwitchedOff: boolean
  /** v3 (D1): the cool-off is on for this assignment; rows, Assigned events and mail happen at sendAfter. */
  waiting: boolean
  queued: number
  sendAfter: string
  /** v3 (L2): the round's mode after this assignment. */
  countsTowardCompliance: boolean
}

/** POST /library/:id/ask-again. */
export interface PolicyAskAgainResult {
  round: number
  /** v3 (E2): the new cycle. */
  cycle: number
  reackWho: PolicyReackWho
  recipients: number
  superseded: number
  /** v3 (E2): Acknowledged Only — pending people whose task and progress were carried onto the new round. */
  carriedForward: number
  dueOn: string
  /** The assignment email is switched OFF in Settings -> Emails: the round opened, and no mail was queued. */
  emailSwitchedOff: boolean
  /** v3 (D1): the rows exist now; only the mail waits for this time (null when sent at once). */
  sendAfter: string | null
  countsTowardCompliance: boolean
}

/** GET /library/:id/late-joiners. */
export interface PolicyLateJoiners {
  count: number
  sampleNames: string[]
  /** At most POLICY_LIMITS.lateJoinerIdsMax ids, for the Assign page pre-fill. */
  recipientIds: string[]
  /** v3 (W3): count is more than recipientIds holds (the Assign page says the list was cut). */
  capped: boolean
}

/** PATCH /library/:id/due-date. */
export interface PolicyDueChangeResult {
  changed: number
  /** Mails the queue took: 0 when the box was unticked, nobody could be mailed, or the email is switched off. */
  emailed: number
  /** People were to be mailed, but the new-due-date email is switched OFF in Settings -> Emails: nothing was queued. */
  emailSwitchedOff: boolean
}

/** POST /library/:id/remind. */
export interface PolicyRemindResult {
  reminded: number
  skippedToday: number
  skippedNotRemindable: number
  /** There were people to remind, but the reminder email is switched OFF in Settings -> Emails: nobody was reminded. */
  emailSwitchedOff: boolean
  /** CR-1: people left out because the mail they would get is switched off (every one of them when `emailSwitchedOff`). */
  skippedEmailOff: number
  /** CR-1: the catalogue keys that are switched off and stopped someone (policy_reminder, policy_due_today, policy_overdue), sorted. */
  emailOffKeys: Array<'policy_reminder' | 'policy_due_today' | 'policy_overdue'>
}

/**
 * GET /options/emails — which Policies emails are switched ON for the caller's workspace (Settings -> Notifications -> Emails),
 * by catalogue key (policy_assigned, policy_reminder, ...). The dialogs that promise mail ask this before they do.
 * A key the read could not answer reads as off (the same fail-closed answer the actions use).
 */
export interface PolicyEmailSwitches {
  emails: Record<string, boolean>
}

/** POST /library/:id/people/extend. */
export interface PolicyExtendResult {
  extended: number
  skippedCovered: number
  skippedNotPending: number
  /** Mails the queue took: 0 when the box was unticked or the email is switched off. */
  emailed: number
  /** People were to be mailed, but the new-due-date email is switched OFF in Settings -> Emails: nothing was queued. */
  emailSwitchedOff: boolean
}

/** Remove, restore: the row's status afterwards. */
export interface PolicyAttestationStatusResult {
  status: PolicyAttestationStatus
}

/** Reset attempts: the row's status afterwards, and whether the person could be told. */
export interface PolicyAttemptsResetResult {
  status: PolicyAttestationStatus
  /** The attempts-reset email is switched OFF in Settings -> Emails: the person got their fresh start, and no mail was queued. */
  emailSwitchedOff: boolean
}

// ── Reports, activity, dashboard (P4) ───────────────────────────────────────

/** GET /library/:id/reports?round= — diagnosis, not totals. */
export interface PolicyDiagnosis {
  stages: Array<{ key: PolicyFunnelStage; count: number; lostFromPrevious: number }>
  mostMissed: Array<{
    questionId: string
    position: number
    text: string
    page: number
    firstAttemptMissRate: number
    firstAttempts: number
    /** firstAttemptMissRate is 25% or more. */
    highlight: boolean
  }>
  hasQuestions: boolean
  /** Least engaged first. */
  departments: Array<{ name: string; compliant: number; assigned: number; rate: number | null }>
}

/** GET /library/:id/activity/series — counts are events, in workspace days. */
export interface PolicyActivitySeries {
  from: string
  to: string
  bucket: PolicyActivityBucket
  points: Array<{ day: string; opened: number; acknowledged: number; reminders: number }>
  timeZone: string
}

/** GET /library/:id/activity/log — this policy's `audit_logs` rows. */
export interface PolicyActivityLogRow {
  at: string
  action: PolicyAuditAction
  entityType: PolicyAuditEntityType
  detailParams: PolicyParams
  actor: { name: string; email: string } | null
}

export type PolicyActivityLogPage = PolicyPage<PolicyActivityLogRow>

/** One department cell of the dashboard heatmap (covered people count as compliant). */
export interface PolicyHeatCell {
  department: string
  compliant: number
  assigned: number
  covered: number
  rate: number | null
}

export interface PolicyHeatTotal {
  compliant: number
  assigned: number
  covered: number
  rate: number | null
}

/** GET /dashboard?department= . */
export interface PolicyDashboard {
  generatedAt: string
  timeZone: string
  today: string
  /** Published policies. */
  policies: number
  /** Active recipients. */
  recipients: number
  acknowledged: number
  acknowledgedRate: number | null
  compliant: number
  assigned: number
  /** People assigned at least one published policy whose every such row is compliant. */
  fullyCompliant: number
  /** … out of the people assigned at least one published policy. */
  fullyCompliantOf: number
  /** Distinct late joiners. */
  notAssignedEveryPolicy: number
  overdue: number
  needsHelp: number
  dueIn14Days: { count: number; policies: Array<{ policyId: string; title: string; count: number }> }
  /** `sampleNames` is [] unless the caller holds read:Recipient (actor.abilities.readRecipient); the count stays. */
  lateJoiners: { count: number; sampleNames: string[]; firstPolicyId: string | null }
  heatmap: {
    departments: string[]
    rows: Array<{
      policyId: string
      title: string
      dueOn: string | null
      overdue: number
      cells: PolicyHeatCell[]
      all: PolicyHeatTotal
    }>
  }
  needsHelpQueue: Array<{
    policyId: string
    attestationId: string
    /** '' unless the caller holds read:Recipient (actor.abilities.readRecipient). */
    name: string
    policyTitle: string
    best: number | null
    since: string
  }>
  /** Top four cells under 80%, overdue first. */
  actFirst: Array<{
    policyId: string
    title: string
    department: string
    rate: number | null
    compliant: number
    assigned: number
    overdueSince: string | null
    dueOn: string | null
  }>
  recentActivity: Array<{ at: string; actor: string; action: PolicyAuditAction; policyTitle: string }>
  /**
   * v3 (L3): the four tiles. inGoodStanding is the workspace's compliance figure over every published policy's
   * compliance rows (L1); pastDue counts people (distinct) and policies; waitingForYou is what the caller may decide
   * emailsWaiting is the cool-off queue (D1), nextAt its earliest send.
   */
  tiles: {
    inGoodStanding: { rate: number | null; people: number; of: number }
    pastDue: { people: number; policies: number }
    waitingForYou: { approvals: number; packs: number; exemptions: number }
    emailsWaiting: { assignments: number; people: number; nextAt: string | null }
  }
  /** v3 (L3): "Needs your attention", in POLICY_ATTENTION_KINDS order, at most POLICY_LIMITS.attentionMax items. */
  attention: PolicyAttentionItem[]
  /** v3 (L3): "Compliance by policy", lowest compliance first, every published policy. */
  complianceByPolicy: PolicyComplianceByPolicyRow[]
}

// ── AI hooks (P9; backend-design §11) ───────────────────────────────────────

export interface PolicyAiKeyPointDraft {
  text: string
  citedPage: number
}

export interface PolicyAiQuestionDraft {
  type: PolicyQuestionType
  text: string
  citedPage: number
  explanation: string
  answers: Array<{ text: string; correct: boolean }>
}

export interface PolicyAiNoteDraft {
  text: string
  /** Change Note only: the material-or-minor hint; null otherwise. */
  changeType: PolicyChangeType | null
}

/** Where on the page a suggestion comes from; the quote is verified server-side before anything is shown. */
export interface PolicyAiCite {
  page: number
  quote: string
  oldPage: number | null
  oldQuote: string | null
}

export interface PolicyAiRunSummary {
  id: string
  kind: PolicyAiRunKind
  status: PolicyAiRunStatus
  creditsCharged: number
  /**
   * 'policy_ai_no_rules' when the run found no clear rules (status 'No Result': answered 200 with no suggestions, not a
   * refusal); null otherwise. A refusal (no credits, AI not set up) is an HTTP error with its own errorCode, never this field.
   */
  errorCode: 'policy_ai_no_rules' | null
}

/** A suggestion is only a proposal until accepted through the version PATCH (`aiSuggestionId`). */
export interface PolicyAiSuggestion {
  id: string
  runId: string
  versionId: string
  kind: PolicyAiSuggestionKind
  /** Updates only: the item the suggestion would replace. */
  targetKeyPointId: string | null
  targetQuestionId: string | null
  sourcePages: number[]
  proposed: PolicyAiKeyPointDraft | PolicyAiQuestionDraft | PolicyAiNoteDraft | null
  cite: PolicyAiCite | null
  language: string
  /** "Question 5 still says 1 hour; page 6 now says 30 minutes." (at most 160 characters). */
  rationale: string
  status: PolicyAiSuggestionStatus
  decidedAt: string | null
  position: number
  createdAt: string
}

export interface PolicyAiRunResult {
  run: PolicyAiRunSummary
  suggestions: PolicyAiSuggestion[]
}

// ── Employee portal and signed link (P7; backend-design §9.4 / §9.5) ───────

/** A portal row's status: Superseded, Removed and (for the person) exempt rows are not listed. */
export type PortalPolicyStatus = 'Not Started' | 'In Progress' | 'Needs Help' | 'Acknowledged' | 'Archived Before Acknowledgment'

/** One policy the signed-in person must acknowledge (or has). */
export interface PortalPolicyItem {
  attestationId: string
  policyId: string
  title: string
  category: PolicyCategory | null
  coverKey: PolicyCoverKey | null
  versionLabel: string
  policyState: 'Published' | 'Archived'
  archivedOn: string | null
  status: PortalPolicyStatus
  dueOn: string
  /** Workspace days until due (negative = overdue). */
  daysLeft: number
  effectiveOn: string
  publishedOn: string
  pageCount: number
  keyPointCount: number
  questionCount: number
  passMark: number | null
  attempts: number
  /** max(5, round(pages * 1.5 + questions)). */
  estimatedMinutes: number
  progress: {
    pagesRead: number
    keyPointsConfirmed: number
    attemptsUsed: number
    passed: boolean
    bestScore: number | null
  }
  owner: { name: string; email: string | null } | null
  /** A new version after an earlier acknowledgment: what changed, and until when the earlier one covers them. */
  update: {
    previousVersionLabel: string
    previousAcknowledgedAt: string
    previousEvidenceId: string
    changeNote: string | null
    changedPages: number[]
    coveredUntil: string
  } | null
  restartedFrom: { versionLabel: string } | null
  /**
   * v3 (H1, X3): an Ask Again: who asked, the note as typed (never translated, D11), and the earlier proof. Never set
   * together with `update` (a new version is an update; the same version asked again is askedAgain).
   */
  askedAgain: {
    byName: string | null
    note: string | null
    startedOn: string
    dueDays: number
    previousAcknowledgedAt: string
    previousEvidenceId: string
  } | null
  /** The row received a `Content Updated` event since it was last opened. */
  minorUpdate: boolean
  acknowledgment: {
    evidenceId: string
    reference: string
    acknowledgedAt: string
    bestScore: number | null
    attempt: number | null
  } | null
  /** v3 (H1): the receipt email went out (an `Email Sent` event with mail 'Receipt' on the row). */
  receiptEmailed: boolean
  /** v3: the policy's document ID and the row's acknowledgment cycle. */
  documentId: string | null
  cycle: number
  /** v3 (H4): the languages the policy is shown in on this row, base first (the "EN · عربي" chip). */
  shownLanguages: PolicyShownLanguage[]
  /** The person's earlier proofs for this policy. */
  history: Array<{ versionLabel: string; evidenceId: string; acknowledgedAt: string }>
}

/** GET /v1/portal/policies — the home ring, the banner and the Policies tab, from one definition. */
export interface PortalPolicyList {
  timeZone: string
  today: string
  retentionYears: number
  summary: {
    current: number
    acknowledged: number
    /** Including Needs Help, even past due (a policy waiting for a reset is still to do). */
    toDo: number
    /** Not Started or In Progress past due. */
    overdue: number
    primaryAttestationId: string | null
    estimatedMinutesTotal: number
    /** Pure progress figures; no points (gamification is not built). */
    policyKeeper: { done: number; total: number }
  }
  items: PortalPolicyItem[]
}

export interface PortalPolicyOption {
  id: string
  text: string
  /** Set on a True Or False option so the portal can translate it. */
  fixed: PolicyFixedAnswer | null
  /** v3 (H5): the pack's translation of the option when shown side by side; null otherwise. */
  secondText: string | null
}

export interface PortalPolicyAttemptItem {
  id: string
  text: string
  /** v3 (H5). */
  secondText: string | null
  type: PolicyQuestionType
  page: number
  /** Shuffled once per attempt and stored with it; true/false and question order are fixed. v3: a `last` answer stays last. */
  options: PortalPolicyOption[]
  /** The saved draft answer ids (Save and Exit keeps unsubmitted answers). */
  draft: string[]
}

export interface PortalPolicyAttempt {
  attemptId: string
  attemptNo: number
  items: PortalPolicyAttemptItem[]
}

/** POST …/attempts/:attemptId/submit. Explanations and correct answers only once passed. */
export interface PortalPolicyAttemptResult {
  score: number
  correct: number
  total: number
  passed: boolean
  attemptsUsed: number
  attemptsLeft: number
  status: PortalPolicyStatus
  /** v3 (H5): secondExplanation = the pack's translation when shown side by side. */
  perQuestion: Array<{ questionId: string; correct: boolean; page: number; explanation: string | null; secondExplanation: string | null }>
  correctAnswerIds: string[] | null
}

export interface PortalPolicyQuestionSet {
  count: number
  passMark: number | null
  attempts: number
  attemptsUsed: number
  bestScore: number | null
  passed: boolean
  currentAttempt: PortalPolicyAttempt | null
  lastResult: PortalPolicyAttemptResult | null
}

export interface PortalPolicyPage {
  number: number
  title: string | null
  text: string | null
  changed: boolean
}

export interface PortalPolicyKeyPoint {
  id: string
  text: string
  /** v3 (H5): the pack's translation when shown side by side (one tick confirms both). */
  secondText: string | null
  page: number
  confirmed: boolean
}

/** GET /v1/portal/policies/tasks/:attestationId (and the signed-link twin): the flow. Nothing is recorded by reading it. */
export interface PortalPolicyTask extends PortalPolicyItem {
  timeZone: string
  today: string
  orgName: string
  identity: { name: string; email: string; department: string }
  /**
   * How this session identifies the person: the sign-in method, or Signed Link on the link routes.
   * Null when the session recorded none (an admin's "View as", or a session older than the column).
   */
  identificationMethod: PolicyIdentificationMethod | null
  channel: 'Portal' | 'Signed Link'
  /** An admin's "View as" session: every step is read-only (403 policy_preview_read_only). Equals recording === 'View As'. */
  preview: boolean
  /** v3 (C1, C2): what this flow records: Live, View As, Preview (admin full-screen) or Test (a test link). */
  recording: PolicyTaskRecording
  /** v3 (C1, C2): set for Preview and Test (attestationId is then the versionId, or the test run id); null otherwise. */
  practice: PolicyPracticeInfo | null
  /** The base-language statement (kept for v1 readers; it is statements[0]). */
  statement: { text: string; sha256: string; language: PolicyLanguage }
  /** v3 (D6, H6): every statement the person acknowledges, base first; acknowledge sends each sha256 back. */
  statements: PolicyStatementShown[]
  /** v3 (H4): how the policy is shown, the second language and its file (GET …/file?language=). */
  display: PolicyTaskDisplay
  /** v3 (H3): a page counts after this long on screen (the row's page_min_ms; the countdown chip reads it). */
  reading: { minPageMs: number }
  /** v3 (L8, L14): the About drawer's retention line. */
  retentionYears: number
  file: { name: string; sizeBytes: number; pageCount: number; sha256: string }
  pages: PortalPolicyPage[]
  readPages: number[]
  keyPoints: PortalPolicyKeyPoint[]
  questions: PortalPolicyQuestionSet
  /** Resume at the first unfinished step; null once acknowledged. */
  nextStep: PolicyFlowStep | null
  /**
   * The receipt email is switched OFF in Settings -> Emails, so acknowledging will not send one (the Acknowledge step
   * must not promise "you'll get a receipt by email"). False once acknowledged: nothing left to promise.
   */
  receiptEmailSwitchedOff: boolean
}

/** POST …/open. */
export interface PortalPolicyStepResult {
  status: PortalPolicyStatus
  nextStep: PolicyFlowStep | null
}

/** POST …/pages/:page/start — nothing is stored; the token is an HMAC the read call must return. */
export interface PortalPolicyPageStart {
  viewToken: string
  minVisibleMs: number
}

/** POST …/pages/:page/read. */
export interface PortalPolicyPageRead {
  readPages: number[]
  readDone: boolean
}

/** PUT …/key-points/:keyPointId. */
export interface PortalPolicyKeyPointsResult {
  keyPoints: Array<{ id: string; confirmed: boolean }>
  allConfirmed: boolean
}

/** PUT …/attempts/:attemptId/answers. */
export interface PortalPolicySavedAnswers {
  savedAt: string
}

/** POST …/acknowledge: the receipt, and the next policy to do. */
export interface PortalPolicyAcknowledgeResult extends PortalPolicyReceipt {
  next: { attestationId: string; title: string } | null
  /**
   * The receipt email was handed to the mail queue by THIS request. False when it is switched off in Settings -> Emails,
   * when the queue took nothing, and on a repeat acknowledge (which queues nothing): the Done screen then says no email
   * is on its way, and the receipt is still there to view.
   */
  receiptEmailQueued: boolean
}

// ── v3 (2026-10-16): shared pieces ──────────────────────────────────────────

/** One language of a policy as the library chips and the Languages card show it (base first). */
export interface PolicyLanguageChip {
  language: string
  dir: 'ltr' | 'rtl'
  state: PolicyLanguageState
  /** The pack the chip stands for (null for the Base chip). */
  packId: string | null
}

/** A language shown to the person (a chip, the "Shown in" line). */
export interface PolicyShownLanguage {
  language: string
  dir: 'ltr' | 'rtl'
}

/** One acknowledgment statement as shown (D6): base template, an ar/hi/fr template, or the pack's approved Statement item. */
export interface PolicyStatementShown {
  language: string
  dir: 'ltr' | 'rtl'
  text: string
  sha256: string
}

/**
 * v3 (H4): how the flow shows the policy. `second` is set only in 'Side By Side' (a Published pack of the row's version
 * in policies.display_language); its file is null when the pack has no PDF of its own (the base PDF is shown alone
 * and the translated text beside it). `chosenBy` is who set what employees see (About: "chosen by …").
 */
export interface PolicyTaskDisplay {
  mode: PolicyDisplayMode
  base: { language: string; dir: 'ltr' | 'rtl' }
  second: {
    language: string
    dir: 'ltr' | 'rtl'
    packId: string
    title: string
    summary: string | null
    file: { name: string; sizeBytes: number; pageCount: number; sha256: string } | null
  } | null
  chosenBy: string | null
}

/** v3 (C1, C2): the practice session behind a Preview or Test flow. */
export interface PolicyPracticeInfo {
  kind: 'Preview' | 'Test'
  policyId: string
  versionId: string
  /** Test only. */
  testRunId: string | null
  /** Test only: the link stops working at this instant (POLICY_LIMITS.testLinkDays). */
  expiresAt: string | null
}

// ── v3: settings (P01) ──────────────────────────────────────────────────────

/** GET /settings (and the PATCH answer): the workspace's Policies settings, or the defaults when no row exists. */
export interface PolicySettingsResponse {
  selfApprovalAllowed: boolean
  enabledLanguages: string[]
  coolOffDefault: boolean
  coolOffMinutes: number
  signedLinksDefault: boolean
  pageMinSeconds: number
  defaultComplianceMode: PolicyComplianceMode
  auditorDefaultDays: number
  auditorMaxDays: number
  /** The value in force today. */
  evidenceRetentionYears: number
  /** L8: a shortening waiting out its POLICY_LIMITS.retentionShortenNoticeDays; null when none. */
  retentionChange: { years: number; effectiveAt: string } | null
  managerDigestEnabled: boolean
  /** False = no row: every value above is the default. */
  stored: boolean
  updatedAt: string | null
  updatedBy: PolicyNameRef | null
  facts: PolicySettingsFacts
  /** L8: the workspace time zone, shown read-only (it is set in the workspace settings). */
  timeZone: string
  can: { update: boolean }
}

/** Workspace facts the Settings page and the "How it works" page print (read, never set, here). */
export interface PolicySettingsFacts {
  /** Members holding create:PolicyApproval (turning self-approval off needs at least two). */
  approverCount: number
  /** The platform reminder schedule (policy-reminder-rules.ts): days after assigning, on the due day, days overdue; sent at sendAt workspace time, weekdays only. */
  reminderSchedule: { afterAssignDays: number[]; onDueDay: boolean; overdueDays: number[]; sendAt: string; weekdaysOnly: boolean }
  /** POLICY_PACK_LANGUAGES with their direction (Settings -> Languages offers these). */
  availableLanguages: Array<{ language: PolicyPackLanguage; dir: 'ltr' | 'rtl' }>
}

// ── v3: details, reviews, as-of (P02, P03) ──────────────────────────────────

/** PATCH /library/:id/details (L11): which metadata fields changed (audited 'Details Changed' with old/new). */
export interface PolicyDetailsChanged {
  changed: PolicyMetadataField[]
}

/** One "Mark as reviewed" (L5; GET /library/:id/reviews, newest first; the History drawer's Review History). */
export interface PolicyReviewRow {
  id: string
  versionId: string
  versionLabel: string
  reviewedAt: string
  reviewedBy: PolicyNameRef | null
  previousReviewOn: string | null
  nextReviewOn: string
  note: string | null
}

/** One person on the as-of page: where they stood at the end of that workspace day. */
export interface PolicyAsOfRow {
  attestationId: string
  recipientId: string
  name: string
  email: string
  department: string
  /** The status at the end of the day ("Status then"). */
  status: PolicyAttestationStatus
  overdue: boolean
  /** "Due then". */
  dueOn: string | null
  acknowledgedAt: string | null
  acknowledgedVersionLabel: string | null
  cycle: number
}

/**
 * GET /library/:id/as-of?day= (F4): the policy as it stood at the END of a past workspace day, rebuilt from the
 * timestamps, the timeline and the proofs. `employmentIsToday`: who is still employed is today's answer (there is no
 * employment history), so the page says so.
 */
export interface PolicyAsOf extends PolicyPage<PolicyAsOfRow> {
  day: string
  /** The first day the policy had a version in force. */
  earliest: string
  today: string
  version: { id: string; label: string; publishedAt: string }
  countsTowardCompliance: boolean
  tiles: {
    assigned: number
    acknowledged: number
    acknowledgedRate: number | null
    overdue: number
    inGoodStanding: number
    complianceRate: number | null
  }
  employmentIsToday: boolean
}

// ── v3: sending, compliance, ask again (P04) ────────────────────────────────

/** An Admin assignment waiting out its cool-off (PolicyDetail.sending; the policy-page banner and the Emails drawer). */
export interface PolicySendingAssignment {
  assignmentId: string
  seqNo: number
  label: string
  people: number
  sendAfter: string
  /** The delivery window may push the first mail later than sendAfter; null when it does not. */
  windowOpensAt: string | null
  createdBy: PolicyNameRef | null
  createdAt: string
  can: { sendNow: boolean; cancel: boolean }
}

/** POST …/assignments/:assignmentId/send-now. */
export interface PolicySendNowResult {
  assignmentId: string
  assigned: number
  skippedAlreadyAssigned: number
  releasedAt: string
  emailSwitchedOff: boolean
  /** C6: the catalogue key of the e-mail this release asked for: the first assignment's, or an Ask Again / new version's request. */
  noticeKey: 'policy_assigned' | 'policy_new_version'
}

/** POST …/assignments/:assignmentId/cancel: nobody in it was assigned. */
export interface PolicyAssignmentCancelResult {
  assignmentId: string
  cancelledAt: string
  people: number
}

/** GET …/rounds/:round/compliance-preview (D3): what "Make it count" will change. */
export interface PolicyCompliancePreview {
  round: number
  versionLabel: string
  people: number
  acknowledged: number
  /** People who would be overdue in the compliance figure at once. */
  overdueNow: number
  rateBefore: number | null
  rateAfter: number | null
}

/** POST …/rounds/:round/count-toward-compliance (one-way). */
export interface PolicyComplianceChangeResult {
  round: number
  countsTowardCompliance: boolean
  changedAt: string
}

/** POST …/ask-again/preview (E2): the dialog's numbers for the chosen `who`. */
export interface PolicyAskAgainPreview {
  reackWho: PolicyReackWho
  /** People who will be asked (get a new row and the email). */
  asked: number
  /** Acknowledged Only: pending people who keep their task and progress (no email). */
  keepTheirTask: number
  /** Exempted people (left out). */
  exempted: number
  dueOn: string
  defaultComplianceMode: PolicyComplianceMode
  coolOffDefault: boolean
  coolOffMinutes: number
}

// ── v3: exemptions (P04) ────────────────────────────────────────────────────

/** The exemption on a person's row (PolicyRecipientRow / PolicyEvidenceRecord). */
export interface PolicyExemptionRef {
  id: string
  status: PolicyExemptionStatus
  scope: PolicyExemptionScope
  endsOn: string
  note: string
}

/** GET /exemptions — one request or exemption. */
export interface PolicyExemptionRow {
  id: string
  scope: PolicyExemptionScope
  /** Null for All Policies. */
  policy: { id: string; title: string } | null
  person: { recipientId: string; name: string; email: string; department: string }
  status: PolicyExemptionStatus
  note: string
  endsOn: string
  requestedBy: PolicyNameRef | null
  requestedAt: string
  decidedBy: PolicyNameRef | null
  decidedAt: string | null
  decisionNote: string | null
  selfApproved: boolean
  withdrawnAt: string | null
  endedAt: string | null
  endedBy: PolicyNameRef | null
  can: { approve: boolean; decline: boolean; withdraw: boolean; end: boolean }
}

export interface PolicyExemptionPage extends PolicyPage<PolicyExemptionRow> {
  statusCounts: Record<PolicyExemptionStatus, number>
}

/** POST /exemptions: one request per person. */
export interface PolicyExemptionRequestResult {
  exemptionIds: string[]
  requested: number
  /** People who already have a live request or exemption for the same scope. */
  skippedAlreadyLive: number
}

/** POST /exemptions/:id/approve | decline | withdraw | end. */
export interface PolicyExemptionDecisionResult {
  id: string
  status: PolicyExemptionStatus
  selfApproved: boolean
  /** Rows set to Exempted (approve) or returned (end). */
  rowsChanged: number
}

// ── v3: dashboard (P03) ─────────────────────────────────────────────────────

/** One "Needs your attention" item (L3). Ids are set where the inline action needs them; the rest are null. */
export interface PolicyAttentionItem {
  kind: PolicyAttentionKind
  policyId: string | null
  title: string | null
  versionId: string | null
  versionLabel: string | null
  packId: string | null
  language: string | null
  exemptionId: string | null
  /** Exemption To Decide only (R4 M3): what the request covers, null on any other item. */
  exemptionScope: PolicyExemptionScope | null
  /** Exemption To Decide only: the last day of the exemption, a workspace day ('yyyy-MM-dd'). */
  endsOn: string | null
  /** Exemption To Decide only: the requester's note; '' unless the caller holds read:Recipient (free text about a named person). */
  note: string | null
  /**
   * Exemption To Decide only (R4b C1): what the caller may do with it, by the same rule as the exemption list's rows (the
   * exempted person never decides their own; reject never on the caller's own request; approving one's own request needs the
   * workspace's self-approval switch; withdraw is the requester's, an assigner's).
   */
  can: { approve: boolean; decline: boolean; withdraw: boolean } | null
  assignmentId: string | null
  attestationId: string | null
  /** People or items behind it (1 for a single approval). */
  count: number
  /** '' unless the caller holds read:Recipient. */
  personName: string
  /** Since when; (Emails Waiting) when they go out; (Review Due) the review-by day, a workspace day; null when it has no time. */
  at: string | null
}

/** One row of "Compliance by policy" (L3). */
export interface PolicyComplianceByPolicyRow {
  policyId: string
  title: string
  documentId: string | null
  assigned: number
  acknowledged: number
  acknowledgedRate: number | null
  overdue: number
  complianceCounts: boolean
  complianceRate: number | null
}

// ── v3: evidence integrity (P05) ────────────────────────────────────────────

/** GET /evidence/integrity (L9): the latest nightly chain check of the workspace. Not a claim of write-once storage. */
export interface PolicyEvidenceIntegrity {
  state: PolicyIntegrityState
  checkedAt: string | null
  proofRows: number
  timelineRows: number
  headSeq: number | null
  headHash: string | null
  firstBreak: { kind: PolicyEvidenceBreakKind; seq: number | null; attestationId: string | null } | null
  timeZone: string
}

// ── v3: language packs (P07) ────────────────────────────────────────────────

/** A pack of a policy (the Languages card, the drafts list). */
export interface PolicyLanguagePackRef {
  packId: string
  language: string
  dir: 'ltr' | 'rtl'
  status: PolicyVersionStatus
  versionId: string
  versionLabel: string
  /** On an older version than the one in force (Needs Updating). */
  outdated: boolean
  hasFile: boolean
  updatedAt: string
}

/** GET /library/:id/languages (and PATCH …/display's answer): the Languages card. */
export interface PolicyLanguages {
  base: { language: string; dir: 'ltr' | 'rtl' }
  /** policies.display_language (null = base only). */
  display: string | null
  /** What employees see NOW ('Side By Side' only with a Published pack on the current version). */
  displayMode: PolicyDisplayMode
  chips: PolicyLanguageChip[]
  packs: PolicyLanguagePackRef[]
  /** Languages turned on in Settings with no pack on the current version yet (the Add a language dialog). */
  addable: string[]
  can: { add: boolean; changeDisplay: boolean }
}

/** One item of a pack, beside the base item it translates. */
export interface PolicyLanguagePackItem {
  id: string
  kind: PolicyPackItemKind
  keyPointId: string | null
  questionId: string | null
  /** 1-based position of the key point or question; null for Title, Summary, Change Note, Statement. */
  position: number | null
  /** The base item now (side by side). */
  baseText: string
  baseExplanation: string | null
  baseAnswers: Array<{ answerId: string; text: string }> | null
  text: string
  explanation: string | null
  answers: Array<{ answerId: string; text: string }> | null
  origin: PolicyPackItemOrigin
  change: PolicyPackItemChange
  /** The base text changed since this item was translated (source_sha256 differs). */
  stale: boolean
  makerEditedAt: string | null
  checkedAt: string | null
  checkedBy: PolicyNameRef | null
}

/** One entry of a pack's "things to fix before submitting". */
export interface PolicyPackIssue {
  code: PolicyPackIssueCode
  params: { kind: PolicyPackItemKind | null; index: number | null }
}

/** GET /library/:id/languages/:packId — the pack editor and its review page. */
export interface PolicyLanguagePackDetail {
  packId: string
  policyId: string
  versionId: string
  versionLabel: string
  language: string
  dir: 'ltr' | 'rtl'
  status: PolicyVersionStatus
  basedOn: { packId: string; versionLabel: string } | null
  file: { name: string; sizeBytes: number; sha256: string; pageCount: number } | null
  previousFile: { name: string } | null
  /** A pack PDF must have exactly this many pages (400 policy_language_file_pages_mismatch). */
  basePageCount: number
  items: PolicyLanguagePackItem[]
  issues: PolicyPackIssue[]
  approval: {
    status: PolicyVersionStatus
    submittedBy: PolicyUserRef | null
    submittedAt: string | null
    approver: PolicyUserRef | null
    note: string | null
    reviewedBy: PolicyNameRef | null
    reviewedAt: string | null
    comment: string | null
    approvedBy: PolicyNameRef | null
    publishedAt: string | null
    selfApproved: boolean
  }
  outdated: boolean
  locked: boolean
  lastAiRun: PolicyAiRunSummary | null
  viewer: {
    canEdit: boolean
    canSubmit: boolean
    canWithdraw: boolean
    canApprove: boolean
    canRequestChanges: boolean
    canDelete: boolean
    isSubmitter: boolean
    isNamedApprover: boolean
    selfApproval: boolean
  }
  timeZone: string
}

/** POST /library/:id/languages and POST …/languages/:language/update: the new Draft pack. */
export interface PolicyLanguagePackCreated {
  packId: string
  language: string
  versionId: string
  /** Items carried unchanged from the earlier pack (update) — 0 for a new pack. */
  carried: number
  /** Items still to translate (New or Changed). */
  toTranslate: number
}

/** POST …/languages/:packId/approve. */
export interface PolicyLanguagePackApproveResult {
  packId: string
  language: string
  status: PolicyVersionStatus
  selfApproved: boolean
  /** Employees now see this language side by side (it is the policy's display language). */
  shownToEmployees: boolean
}

/** POST …/languages/:packId/ai/translate (A4): the run and the pack afterwards. Nothing changed = no run, no charge. */
export interface PolicyPackAiResult {
  run: PolicyAiRunSummary | null
  translated: number
  skippedUnchanged: number
  pack: PolicyLanguagePackDetail
}

/** POST …/versions/:versionId/ai/details (A1 "Fill with AI"): the fields filled (only empty ones) and the version after. */
export interface PolicyAiDetailsResult {
  run: PolicyAiRunSummary
  filled: PolicyPrefillField[]
  version: PolicyVersionDetail
}

// ── v3: practice, preview and tests (P08) ───────────────────────────────────

/** POST …/preview/receipt and the test link's receipt: a SAMPLE marked PREVIEW or TEST; nothing is recorded. */
export interface PolicyPracticeReceipt extends PortalPolicyReceipt {
  practice: 'Preview' | 'Test'
}

/** POST / GET …/versions/:versionId/tests (C2): a test sent to the caller's own address. */
export interface PolicyTestRunRow {
  id: string
  versionId: string
  versionLabel: string
  toEmail: string
  mailKey: string
  status: PolicyTestRunStatus
  /** Not Sent: why (the mail outcome code); null otherwise. */
  outcome: string | null
  sentAt: string | null
  openedAt: string | null
  finishedAt: string | null
  score: number | null
  expiresAt: string
  createdAt: string
}

// ── v3: external auditors and audit reports (P09) ───────────────────────────

/** GET /auditors — one invited auditor (Settings -> Auditor access). */
export interface PolicyAuditorRow {
  id: string
  email: string
  name: string | null
  organisation: string | null
  policies: Array<{ id: string; title: string; documentId: string | null }>
  /** The last workspace day of access, and the instant it ends. */
  accessUntil: string
  accessEndsAt: string
  status: PolicyAuditorStatus
  invitedBy: PolicyNameRef | null
  invitedAt: string
  lastInvitedAt: string
  lastSignedInAt: string | null
  revokedAt: string | null
  revokedBy: PolicyNameRef | null
  can: { change: boolean; revoke: boolean; resend: boolean }
}

/** POST /public/policy-audit/sign-in/code: the same answer whether or not the address matched (nothing revealed). */
export interface PolicyAuditorCodeSent {
  sent: boolean
  expiresInMinutes: number
}

/** POST /public/policy-audit/sign-in/verify: the session token (sent back as X-Policy-Audit-Token). */
export interface PolicyAuditorSession {
  token: string
  expiresAt: string
  me: PolicyAuditorMe
}

/** GET /public/policy-audit/me — who the auditor is and what they may see ("Your access", "What you can see"). */
export interface PolicyAuditorMe {
  grantId: string
  name: string | null
  email: string
  organisation: string | null
  orgName: string
  workspaceName: string
  accessUntil: string
  accessEndsAt: string
  timeZone: string
  today: string
  policies: Array<{ id: string; title: string; documentId: string | null }>
}

/** One granted policy on the auditor's Policies list. */
export interface PolicyAuditorPolicyRow {
  id: string
  title: string
  documentId: string | null
  category: PolicyCategory | null
  status: 'Published' | 'Archived'
  currentVersion: { id: string; label: string }
  figures: PolicyFigures
}

/** GET /public/policy-audit/policies/:policyId — read-only: the policy, its released versions and its numbers. */
export interface PolicyAuditorPolicy {
  policy: PolicyAuditorPolicyRow
  /** Published, Superseded and Archived versions only (never a draft). */
  versions: PolicyVersionRow[]
  keyPoints: PolicyKeyPoint[]
  questions: PolicyQuestion[]
  rounds: PolicyRoundRef[]
  timeZone: string
  today: string
}

/** GET /public/policy-audit/overview. */
export interface PolicyAuditorOverview {
  me: PolicyAuditorMe
  policies: PolicyAuditorPolicyRow[]
  integrity: PolicyEvidenceIntegrity
  reports: PolicyAuditReportRow[]
}

/** One file of a built report (download through the API; never a public URL). */
export interface PolicyAuditReportFile {
  kind: PolicyAuditReportFileKind
  name: string
  sizeBytes: number
  sha256: string
  /** Data rows of a CSV; null for the PDF, a folder or the ZIP. */
  rows: number | null
}

/** The options a report was built with (policy_audit_reports.options, as read back). */
export interface PolicyAuditReportOptionsView {
  scope: PolicyAuditReportScope
  category: PolicyCategory | null
  controlTag: string | null
  people: PolicyAuditReportPeople
  emailCount: number
  includeReceipts: boolean
}

/** One audit report (the internal builder's list and the auditor's). Built in the background (L10). */
export interface PolicyAuditReportRow {
  id: string
  status: PolicyAuditReportStatus
  requesterKind: PolicyAuditRequesterKind
  requestedBy: { name: string; email: string | null }
  policies: Array<{ id: string; title: string }>
  periodFrom: string
  periodTo: string
  timeZone: string
  language: string
  options: PolicyAuditReportOptionsView
  files: PolicyAuditReportFile[]
  /** Failed: the reason code; null otherwise. */
  errorCode: string | null
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  /** Ready: the files stop downloading at this instant (POLICY_LIMITS.auditReportLinkDays). */
  expiresAt: string | null
  /**
   * R1 #3: what the reader may do. `remove`: the requester or an approver, on an internal report only (an external
   * auditor's report is never deleted on the internal route; always false in the auditor's area).
   */
  can: { remove: boolean }
}

// ═════════════════════════════════════════════════════════════════════════════
// WEB-ONLY SHAPES — answers the API sends without a declaration of their own
// ═════════════════════════════════════════════════════════════════════════════

/** GET …/versions/:versionId/issues. */
export interface PolicyIssuesResult {
  issues: PolicyIssue[]
}

// ═════════════════════════════════════════════════════════════════════════════
// REQUESTS — bodies and queries (mirror the request DTOs; not compared by the guard)
// ═════════════════════════════════════════════════════════════════════════════

export type PolicySortOrder = 'ASC' | 'DESC'

/** GET /library?section=published (ListPolicyLibraryQueryDto). `limit` is 10, 25 or 50. */
export interface PolicyLibraryQuery {
  search?: string
  page?: number
  limit?: number
  sortBy?: PolicyLibrarySortField
  sortOrder?: PolicySortOrder
  /** v3 (F2). `search` also matches the document ID. */
  category?: PolicyCategory
}

/** GET /library?section=drafts. */
export interface PolicyDraftsQuery {
  search?: string
  draftStatus?: PolicyDraftStatus
  /** v3 (F2): versions or language packs; absent = both. */
  draftKind?: PolicyDraftKind
  page?: number
  limit?: number
}

/** GET /library?section=archived. */
export interface PolicyArchivedQuery {
  search?: string
  page?: number
  limit?: number
  /** v3 (F2). */
  category?: PolicyCategory
}

/** PATCH /library/:id — the title only while the policy is a Draft (`policy_title_locked`). */
export interface UpdatePolicyBody {
  title?: string
  coverKey?: PolicyCoverKey | null
}

export interface PolicyAnswerInput {
  id?: string
  text: string
  correct: boolean
}

export interface PolicyKeyPointInput {
  id?: string
  text: string
  citedPage: number
  aiSuggestionId?: string
}

export interface PolicyQuestionInput {
  id?: string
  type: PolicyQuestionType
  text: string
  citedPage: number
  explanation: string
  answers: PolicyAnswerInput[]
  aiSuggestionId?: string
}

/**
 * PATCH …/versions/:versionId — the editor's autosave: any of these. `keyPoints` and
 * `questions` REPLACE the whole list in array order (an id missing is removed, an id of a
 * removed item is restored: Undo; `aiSuggestionId` marks that suggestion Accepted). `label` is refused
 * before anything is written when it is not major.minor (400 `policy_version_number_invalid` {example}) or not
 * higher than the version in force (409 `policy_version_number_not_higher` {current}): the stored number is always valid.
 */
export interface UpdatePolicyVersionBody {
  title?: string
  category?: PolicyCategory | null
  ownerUserId?: string | null
  effectiveOn?: string | null
  reviewBy?: string | null
  summary?: string
  label?: string
  changeType?: PolicyChosenChangeType
  changeNote?: string
  reackDays?: number
  passMark?: number
  attemptsAllowed?: number
  keyPoints?: PolicyKeyPointInput[]
  questions?: PolicyQuestionInput[]
  /** v3 (A1): only while the policy is a Draft. */
  documentId?: string | null
  managementApproval?: string | null
  managementApprovedOn?: string | null
  controlTags?: string[]
  /** v3 (D8): Material only; false = acknowledgment only. */
  countsTowardCompliance?: boolean
  /** v3 (L12): Revision only. */
  revisionOverrideReason?: string | null
}

/** v3 (E1): POST /library/:id/versions — 'Material' = New version, 'Minor' = Revision. Absent = 'Material'. */
export interface CreatePolicyVersionBody {
  changeType?: PolicyChosenChangeType
}

export interface SubmitPolicyVersionBody {
  approverUserId: string
  note?: string
}

export interface RequestPolicyChangesBody {
  comment: string
}

export interface RunPolicyAiBody {
  /** v3: the suggestion kinds only ('Details' and 'Translation' have their own routes). */
  kind: PolicyAiSuggestionRunKind
  pages?: number[]
}

export interface PolicyAiSuggestionsQuery {
  status?: PolicyAiSuggestionStatus
  kind?: PolicyAiSuggestionKind
}

export interface PolicyActivitySeriesQuery {
  round?: number
  bucket?: PolicyActivityBucket
}

export interface PolicyActivityLogQuery {
  page?: number
  limit?: number
}

export interface PolicyPeopleQuery {
  round?: number
  status?: PolicyPeopleStatusFilter
  department?: string
  domainId?: string
  search?: string
  page?: number
  limit?: number
  sortBy?: PolicyPeopleSortField
  sortOrder?: PolicySortOrder
}

/** Who gets the policy: the shared recipient picker's own shape (RecipientsFormData), stored verbatim. */
export interface PolicyAudience {
  selectedRecipientIds: string[]
  excludeRecipientIds: string[]
  recipientGroups: Array<{ id: string; name?: string }>
  smartGroupIds: string[]
  bulkPasteRaw: string
  selectAll: boolean
}

export type PolicyDueInput = { mode: 'Days From Today'; days: number } | { mode: 'Fixed Date'; on: string }

/** POST /library/:id/assignments/preview, and the same body to POST …/assignments. */
export interface AssignPolicyBody {
  audience: Partial<PolicyAudience>
  due: PolicyDueInput
  reminders: PolicyReminderSwitches
  /** v3 (D1): POST …/assignments only; absent = the workspace's cool_off_default. */
  waitBeforeSending?: boolean
  /** v3: absent = the workspace's signed_links_default. */
  signedLink?: boolean
  /** v3 (L2): only when this assignment opens its round (preview.complianceChoice); absent = the workspace default. */
  countsTowardCompliance?: boolean
}

/** v3 (E2): POST …/ask-again/preview. */
export interface PreviewAskPolicyAgainBody {
  who?: PolicyReackWho
  dueDays: (typeof POLICY_REACK_DAYS)[number]
}

/** v3 (E2, D11): POST …/ask-again. Absent `who` = Everyone Assigned. */
export interface AskPolicyAgainBody {
  dueDays: (typeof POLICY_REACK_DAYS)[number]
  who?: PolicyReackWho
  /** "Note in the email", shown as typed. */
  note?: string | null
  countsTowardCompliance?: boolean
  waitBeforeSending?: boolean
}

/** POST …/remind — absent `attestationIds` = every remindable row; an empty list is `policy_selection_empty`. */
export interface RemindPolicyBody {
  attestationIds?: string[]
}

export interface ExtendPolicyBody {
  attestationIds: string[]
  days: 7 | 14 | 30
  emailPending: boolean
}

export interface RemovePolicyRecipientBody {
  reason: PolicyRemovalReason
  note?: string | null
}

export interface ChangePolicyDueDateBody {
  dueOn: string
  emailPending: boolean
}

export type UpdatePolicyRemindersBody = Partial<PolicyReminderSwitches>

export interface PortalPolicyPageStartBody {
  fileSha256: string
}

export interface PortalPolicyPageReadBody {
  fileSha256: string
  viewToken: string
  visibleMs: number
}

export interface PortalPolicyOpenBody {
  source: PolicyOpenSource
}

export interface PortalPolicyKeyPointBody {
  confirmed: boolean
}

/** Answers by question id: the ids of the answers chosen (one for a single choice, several for a multiple choice). */
export interface PortalPolicyAnswersBody {
  answers: Record<string, string[]>
}

/**
 * `statementSha256` is the base statement's hash (task.statements[0]) and `fileSha256` the base file's. v3 (D6, H6): side
 * by side, also the pack shown, its statement's hash (task.statements[1]) and its PDF's hash when it has one. `locale`
 * is accepted and ignored.
 */
export interface PortalPolicyAcknowledgeBody {
  statementSha256: string
  fileSha256: string
  packId?: string
  packStatementSha256?: string
  packFileSha256?: string
  locale?: PolicyLanguage
}

// ── v3 requests (2026-10-16, C0) ────────────────────────────────────────────

/** POST /library/:id/archive (L4). */
export interface ArchivePolicyBody {
  reason: PolicyArchiveReason
  note?: string | null
  replacedByPolicyId?: string | null
}

/** PATCH /library/:id/details (L11): any subset. */
export interface UpdatePolicyDetailsBody {
  ownerUserId?: string | null
  category?: PolicyCategory | null
  reviewBy?: string | null
  controlTags?: string[]
}

/** POST /library/:id/reviews (L5). */
export interface MarkPolicyReviewedBody {
  nextReviewOn: string
  note?: string | null
}

/** GET /library/:id/as-of (F4). */
export interface PolicyAsOfQuery {
  day: string
  status?: PolicyPeopleStatusFilter
  department?: string
  search?: string
  page?: number
  limit?: number
}

/** PATCH /settings (F5): any subset. */
export interface UpdatePolicySettingsBody {
  selfApprovalAllowed?: boolean
  enabledLanguages?: string[]
  coolOffDefault?: boolean
  coolOffMinutes?: number
  signedLinksDefault?: boolean
  pageMinSeconds?: (typeof POLICY_PAGE_MIN_SECONDS)[number]
  defaultComplianceMode?: PolicyComplianceMode
  auditorDefaultDays?: number
  auditorMaxDays?: number
  evidenceRetentionYears?: (typeof POLICY_RETENTION_YEARS)[number]
  managerDigestEnabled?: boolean
}

/** POST /exemptions (E3): policyId null = All Policies. */
export interface RequestPolicyExemptionsBody {
  recipientIds: string[]
  policyId: string | null
  note: string
  endsOn: string
}

/** POST /exemptions/:id/approve | end. */
export interface DecidePolicyExemptionBody {
  note?: string | null
}

/** POST /exemptions/:id/decline: the note is required. */
export interface DeclinePolicyExemptionBody {
  note: string
}

/** GET /exemptions. */
export interface PolicyExemptionsQuery {
  status?: PolicyExemptionStatus
  scope?: PolicyExemptionScope
  policyId?: string
  recipientId?: string
  search?: string
  page?: number
  limit?: number
}

/** POST /library/:id/languages (A3). */
export interface CreatePolicyLanguagePackBody {
  language: string
}

/** One item in a pack PATCH: only the fields sent change. */
export interface PolicyPackItemInput {
  id: string
  text?: string
  explanation?: string | null
  answers?: Array<{ answerId: string; text: string }>
  checked?: boolean
}

/** PATCH /library/:id/languages/:packId (autosave). */
export interface UpdatePolicyLanguagePackBody {
  items: PolicyPackItemInput[]
}

/** POST …/languages/:packId/submit. */
export interface SubmitPolicyLanguagePackBody {
  approverUserId: string
  note?: string
}

/** POST …/languages/:packId/ai/translate. */
export interface TranslatePolicyLanguagePackBody {
  itemIds?: string[]
}

/** PATCH /library/:id/display (L17): null = base only. */
export interface SetPolicyDisplayLanguageBody {
  language: string | null
}

/** GET …/versions/:versionId/preview (C1). */
export interface PolicyPreviewQuery {
  locale?: string
  language?: string
}

/** POST …/preview/attempts (and the test link's). */
export interface PolicyPracticeAttemptBody {
  attemptNo: number
}

/** POST …/preview/grade (and the test link's). */
export interface PolicyPracticeGradeBody {
  attemptNo: number
  answers: Record<string, string[]>
}

/** POST …/preview/receipt (and the test link's). */
export interface PolicyPracticeReceiptBody {
  score?: number | null
  attemptsUsed: number
}

/** POST …/versions/:versionId/tests (C2). */
export interface CreatePolicyTestRunBody {
  language?: string | null
}

/** POST /public/policy-tests/:testId/:sig/finished. */
export interface PolicyTestFinishedBody {
  score?: number | null
}

/** POST /auditors (G2). */
export interface InvitePolicyAuditorBody {
  email: string
  name?: string | null
  organisation?: string | null
  days?: number
  policyIds: string[]
}

/** PATCH /auditors/:grantId. */
export interface UpdatePolicyAuditorBody {
  days?: number
  policyIds?: string[]
  name?: string | null
  organisation?: string | null
}

/** POST /public/policy-audit/sign-in/code (G1). */
export interface PolicyAuditorCodeBody {
  grantId: string
  sig: string
  email: string
}

/** POST /public/policy-audit/sign-in/verify. */
export interface PolicyAuditorVerifyBody extends PolicyAuditorCodeBody {
  code: string
}

/** POST /audit-reports (G6, L10: the internal builder). */
export interface CreatePolicyAuditReportBody {
  scope: PolicyAuditReportScope
  policyIds?: string[]
  category?: PolicyCategory
  controlTag?: string
  people: PolicyAuditReportPeople
  emailsRaw?: string
  includeReceipts: boolean
  from: string
  to: string
  language: PolicyLanguage
}

/** POST /public/policy-audit/reports (G5: the auditor's builder; granted policies only). */
export interface CreateAuditorReportBody {
  policyIds?: string[]
  from: string
  to: string
  language: PolicyLanguage
}


// ── Small pure helpers every policies screen uses ───────────────────────────

/** The interface language as a policy language (the four are the same set); English when it is not one. */
export function toPolicyLanguage(locale: string | null | undefined): PolicyLanguage {
  const value = (locale ?? '').slice(0, 2).toLowerCase()
  return isPolicyLanguage(value) ? value : 'en'
}

/**
 * A version label as people read it, always both parts: '1.0' → 'v1.0', '1.10' → 'v1.10'.
 * (admin-screens D5: the old displayVersion() turned 2.0 into 2 and is gone.) Inside a
 * translated sentence use t('policies.version.label', { version: label }) instead.
 */
export function versionLabel(label: string | null | undefined): string {
  return label ? `v${label}` : ''
}

/**
 * v3 (D9, E1): the parts of an API version label. The API sends '1.0' for a new version and '1.0 rev 2' for a
 * Revision; a screen words the revision through i18n (never prints the English "rev" in another language).
 */
export function policyVersionParts(label: string | null | undefined): { number: string; revision: number } {
  const m = /^(\d+\.\d+)(?: rev (\d+))?$/.exec((label ?? '').trim())
  return m ? { number: m[1], revision: m[2] ? Number(m[2]) : 1 } : { number: label ?? '', revision: 1 }
}

/** The cover to draw: the stored key, else the category's default, else the general cover. */
export function resolvePolicyCoverKey(coverKey: PolicyCoverKey | null | undefined, category: PolicyCategory | null | undefined): PolicyCoverKey {
  if (coverKey && (POLICY_COVER_KEYS as readonly string[]).includes(coverKey)) return coverKey
  if (category && category in POLICY_DEFAULT_COVER_BY_CATEGORY) return POLICY_DEFAULT_COVER_BY_CATEGORY[category]
  return 'policy-general'
}

/** First and last four hex characters of a fingerprint, for tight tables ("7f3a…c91e"). Render dir="ltr". */
export function shortFingerprint(hex: string | null | undefined): string {
  if (!hex) return ''
  return hex.length <= 8 ? hex : `${hex.slice(0, 4)}…${hex.slice(-4)}`
}

/** The evidence card's fingerprint: first 16 characters … last 8 ("7f3a91c0e2b4d658…5d1c8e02"). Render dir="ltr". */
export function evidenceFingerprint(hex: string | null | undefined): string {
  if (!hex) return ''
  return hex.length <= 24 ? hex : `${hex.slice(0, 16)}…${hex.slice(-8)}`
}

/** Is this a status that still owes an acknowledgment? */
export function isPendingAttestationStatus(status: string | null | undefined): boolean {
  return (POLICY_PENDING_ATTESTATION_STATUSES as readonly string[]).includes(status ?? '')
}

/** Can a maker still edit a version in this state? */
export function isEditableVersionStatus(status: string | null | undefined): boolean {
  return (POLICY_EDITABLE_VERSION_STATUSES as readonly string[]).includes(status ?? '')
}
